import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  TextField,
  Button,
  Stack,
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Autocomplete,
  Snackbar,
} from '@mui/material';
import { Iconify } from '../../components/iconify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchPromotionById,
  updatePromotion,
  fetchProductsList,
  fetchCategories,
  fetchBrands,
  fetchPromotionChannels,
} from '../../../../../services/PromotionServices';

// Import promotion type components
import BuyXGetYFree from './BuyXGetYFree';
import BuyXGetYDiscount from './BuyXGetYDiscount';
import BuyXDiscount from './BuyXDiscount';
import {
  findPromotionProductListIndex,
  shiftProductErrorsAfterRemove,
  clearProductErrorKeys,
} from './promotionProductErrors';
import MultiLayerOffer from './MultiLayerOffer';
import {
  applySlabGetQuantityCompulsoryErrors,
  buildSlabGetQuantityCompulsoryErrors,
  toApiPromotionPricingType,
  fromApiPromotionPricingType,
  resolveBuyProductPricingFields,
  resolveMultiLayerBuyPricingTypeFromApi,
  resolveMultiLayerGetPricingTypeFromApi,
} from './promotionPricingHelpers';
import GroupOffer from './GroupOffer';
import { mapPromotionApiStockFields, getProductSelectPopupStock } from './promotionStockHelpers';
import '../../../../common/commonStyles.css';

const parseNumber = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const parseInteger = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : 0;
};

const normalizeDiscountType = (value) => {
  const raw = (value || '').toString().trim();
  const normalized = raw.toLowerCase();
  if (raw === 'PERCENTAGE' || ['percentage', 'percent', 'percentage_value'].includes(normalized)) return 'percentage';
  if (raw === 'FIXED_AMOUNT' || raw === 'FIXED' || ['fixed', 'fixed_amount', 'fixed-amount', 'amount', 'value_off', 'value-off'].includes(normalized)) return 'fixed';
  if (['set_value', 'set-value'].includes(normalized)) return 'set-value';
  return normalized;
};

const normalizeItemDiscountType = (value, { allowNoDiscount = false } = {}) => {
  const normalized = normalizeDiscountType(value);
  if (normalized === 'percentage' || normalized === 'fixed') return normalized;
  return allowNoDiscount ? 'no-discount' : 'percentage';
};

const normalizePieceBoxPricingSelect = (value, fallback = 'sales') => {
  const raw = value != null ? String(value).trim() : '';
  const lower = raw.toLowerCase().replace(/-/g, '_');
  if (
    lower === 'offer' ||
    lower === 'offer_price' ||
    lower === 'off' ||
    raw.toUpperCase() === 'OFFER'
  ) {
    return 'offer';
  }
  if (
    lower === 'sales' ||
    lower === 'sales_price' ||
    lower === 'standard' ||
    raw.toUpperCase() === 'SALES_PRICE' ||
    raw.toUpperCase() === 'SALES'
  ) {
    return 'sales';
  }
  const fb = fallback != null ? String(fallback).trim().toLowerCase() : 'sales';
  return fb === 'offer' ? 'offer' : 'sales';
};

const pickPositive = (...vals) => {
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const getForcedPrice = (product, type = 'Box', mode = 'sales') => {
  if (type === 'Piece') {
    if (mode === 'offer') {
      return Number(pickPositive(product.pieceOfferPrice, product.offerPiecePrice, product.offerPrice, 0));
    }
    return Number(
      pickPositive(
        product.pieceSalesPrice,
        product.PiecePrice,
        product.piece_sales_price,
        product.salesPrice,
        product.price,
        0
      )
    );
  }
  if (mode === 'offer') {
    return Number(pickPositive(product.boxOfferPrice, product.offerBoxPrice, product.offerPrice, 0));
  }
  return Number(pickPositive(product.boxSalesPrice, product.salesPrice, product.box_sales_price, product.price, 0));
};

const getMultiLayerBuyPricingMode = (product, priceKind = 'Box', defaultPricingType = 'sales') => {
  if (priceKind === 'Piece') {
    const pieceMode = product.piecePricingType ?? product.piece_pricing_type;
    if (pieceMode && pieceMode !== '' && pieceMode !== 'undefined' && pieceMode !== 'null') {
      return pieceMode;
    }
  } else {
    const boxMode = product.boxPricingType ?? product.box_pricing_type;
    if (boxMode && boxMode !== '' && boxMode !== 'undefined' && boxMode !== 'null') {
      return boxMode;
    }
  }
  const fallback = product.pricingType ?? product.pricing_type;
  if (fallback && fallback !== '' && fallback !== 'undefined' && fallback !== 'null') {
    return fallback;
  }
  const resolvedDefault =
    defaultPricingType &&
    defaultPricingType !== '' &&
    defaultPricingType !== 'undefined' &&
    defaultPricingType !== 'null'
      ? defaultPricingType
      : 'sales';
  return resolvedDefault === 'offer' ? 'offer' : 'sales';
};

const getMinMultiLayerBuyPrice = (products, priceKind = 'Box', defaultPricingType = 'sales') => {
  if (!Array.isArray(products) || products.length === 0) return 0;
  const prices = products
    .map((product) =>
      getForcedPrice(
        product,
        priceKind,
        getMultiLayerBuyPricingMode(product, priceKind, defaultPricingType)
      )
    )
    .filter((price) => Number.isFinite(price) && price > 0);
  if (prices.length === 0) return 0;
  return Math.min(...prices);
};

const ProductSelectionModal = ({
  open,
  onClose,
  onConfirm,
  title,
  selectedProducts = [],
  productTypeFilter = null,
  showToast,
  alreadyAddedProducts = [],
  firstBuyProduct = null,
  isGetForSlab = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [brands, setBrands] = useState(['All']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentFirstProduct, setCurrentFirstProduct] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedItems(selectedProducts);
      setSearchQuery('');
      setDebouncedSearchQuery('');
      setSelectedCategory('All');
      setSelectedBrand('All');
      setCurrentPage(1);
      // Initialize currentFirstProduct from firstBuyProduct (from parent) or from selectedProducts
      if (firstBuyProduct) {
        console.log('MultiLayerOffer: Modal opened with firstBuyProduct from parent:', firstBuyProduct);
        setCurrentFirstProduct(firstBuyProduct);
      } else if (selectedProducts && selectedProducts.length > 0 && productTypeFilter) {
        console.log('MultiLayerOffer: Modal opened with selectedProducts, using first product:', selectedProducts[0]);
        setCurrentFirstProduct(selectedProducts[0]);
      } else {
        setCurrentFirstProduct(null);
      }
      loadCategories();
      loadBrands();
    }
  }, [open, selectedProducts, firstBuyProduct, productTypeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Watch for when first product is selected/changed in modal (for MultiLayerOffer)
  // Skip this for get products (getForSlab) - no need to update currentFirstProduct or reload API
  useEffect(() => {
    if (open && productTypeFilter && !isGetForSlab) {
      if (selectedItems.length === 0) {
        // No products selected in modal - reset currentFirstProduct (but keep firstBuyProduct from parent)
        if (currentFirstProduct && !firstBuyProduct) {
          setCurrentFirstProduct(null);
          setCurrentPage(1);
        }
      } else if (selectedItems.length >= 1) {
        // First product selected - update currentFirstProduct and reload API
        const firstSelected = selectedItems[0];
        const currentProductId = currentFirstProduct ? (currentFirstProduct.product_id || currentFirstProduct.id) : null;
        const newProductId = firstSelected.product_id || firstSelected.id;

        // Update if it's a different product (different ID) or if currentFirstProduct is null
        if (!currentFirstProduct || currentProductId !== newProductId) {
          console.log('MultiLayerOffer: Updating currentFirstProduct from selectedItems:', firstSelected);
          setCurrentFirstProduct(firstSelected);
          // Reset to page 1 and reload with pack parameter
          setCurrentPage(1);
        }
      }
    }
  }, [selectedItems, open, productTypeFilter, currentFirstProduct, firstBuyProduct, isGetForSlab]);

  // Only include currentFirstProduct in dependency when not getForSlab
  const effectiveFirstProduct = useMemo(() => {
    return isGetForSlab ? null : currentFirstProduct;
  }, [isGetForSlab, currentFirstProduct]);

  useEffect(() => {
    if (open) {
      loadProducts();
    }
  }, [open, currentPage, rowsPerPage, debouncedSearchQuery, selectedCategory, selectedBrand, firstBuyProduct, effectiveFirstProduct]);

  const loadCategories = async () => {
    try {
      const response = await fetchCategories();
      if (response.success && response.data) {
        setCategories(['All', ...response.data.map((cat) => cat.name)]);
      }
    } catch (error) {
    }
  };

  const loadBrands = async () => {
    try {
      const response = await fetchBrands();
      if (response.success && response.data) {
        setBrands(['All', ...response.data.map((brand) => brand.name)]);
      }
    } catch (error) {
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        pageSize: rowsPerPage,
      };

      if (debouncedSearchQuery) {
        params.search = debouncedSearchQuery;
      }
      if (selectedCategory !== 'All') {
        params.category = selectedCategory;
      }
      if (selectedBrand !== 'All') {
        params.brand = selectedBrand;
      }

      if (productTypeFilter && productTypeFilter !== null && productTypeFilter !== '') {
        const productTypeMap = {
          'box-only': 'box',
          'piece-only': 'piece',
          'both': 'both'
        };
        params.producttype = productTypeMap[productTypeFilter] || productTypeFilter;
      }

      // For get products (getForSlab): Pass box parameter only when productTypeFilter is box-only
      if (isGetForSlab && productTypeFilter === 'box-only') {
        params.box = '1';
      }

      const response = await fetchProductsList(params);

      if (response.success && response.data) {
        let filteredProducts = response.data.products || [];

        if (productTypeFilter) {
          filteredProducts = filteredProducts.filter(product => {
            const split = product.split;
            if (productTypeFilter === 'box-only') {
              return split === 0 || split === 2;
            } else if (productTypeFilter === 'piece-only') {
              return split === 1 || split === 2;
            } else if (productTypeFilter === 'both') {
              return split === 0 || split === 1 || split === 2;
            }
            return true;
          });
        }

        // For Multi-Layer Offer: If first product is selected, filter by price only (pack is handled by API)
        // Skip price filtering for get products (getForSlab)
        if (firstBuyProduct && !isGetForSlab) {
          filteredProducts = filteredProducts.filter(product => {

            const epsilon = 0.005;
            let priceMatch = false;

            if (productTypeFilter === 'box-only') {
              const productBoxPrice = getForcedPrice(product, 'Box', 'sales');
              const firstProductBoxPrice = getForcedPrice(firstBuyProduct, 'Box', 'sales');
              priceMatch = Math.abs(productBoxPrice - firstProductBoxPrice) < epsilon;
            } else if (productTypeFilter === 'piece-only') {
              const productPiecePrice = getForcedPrice(product, 'Piece', 'sales');
              const firstProductPiecePrice = getForcedPrice(firstBuyProduct, 'Piece', 'sales');
              priceMatch = Math.abs(productPiecePrice - firstProductPiecePrice) < epsilon;
            } else if (productTypeFilter === 'both') {
              const productBoxPrice = getForcedPrice(product, 'Box', 'sales');
              const firstProductBoxPrice = getForcedPrice(firstBuyProduct, 'Box', 'sales');
              const productPiecePrice = getForcedPrice(product, 'Piece', 'sales');
              const firstProductPiecePrice = getForcedPrice(firstBuyProduct, 'Piece', 'sales');
              priceMatch =
                Math.abs(productBoxPrice - firstProductBoxPrice) < epsilon &&
                Math.abs(productPiecePrice - firstProductPiecePrice) < epsilon;
            }

            return priceMatch;
          });
        }

        // Auto-skip blank pages when filtering by product type leads to zero results
        if (productTypeFilter && filteredProducts.length === 0 && response?.data?.pagination) {
          const basePagination = response.data.pagination;
          let nextPage = basePagination.current_page + 1;
          let found = false;
          while (nextPage <= (basePagination.total_pages || nextPage - 1)) {
            const nextParams = { ...params, page: nextPage };
            const nextResp = await fetchProductsList(nextParams);
            if (!(nextResp?.success && nextResp?.data)) break;
            let nextProducts = (nextResp.data.products || []);
            if (productTypeFilter) {
              nextProducts = nextProducts.filter(p => {
                const split = p.split;
                if (productTypeFilter === 'box-only') return split === 0 || split === 2;
                if (productTypeFilter === 'piece-only') return split === 1 || split === 2;
                if (productTypeFilter === 'both') return split === 0 || split === 1 || split === 2;
                return true;
              });
            }
            // Skip price filtering for get products (getForSlab)
            if (firstBuyProduct && nextProducts.length && !isGetForSlab) {
              nextProducts = nextProducts.filter(p => {
                let priceMatch = false;
                if (productTypeFilter === 'box-only') {
                  const pb = getForcedPrice(p, 'Box', 'sales');
                  const fb = getForcedPrice(firstBuyProduct, 'Box', 'sales');
                  priceMatch = Math.abs(pb - fb) < 0.005;
                } else if (productTypeFilter === 'piece-only') {
                  const pp = getForcedPrice(p, 'Piece', 'sales');
                  const fp = getForcedPrice(firstBuyProduct, 'Piece', 'sales');
                  priceMatch = Math.abs(pp - fp) < 0.005;
                } else if (productTypeFilter === 'both') {
                  const pb = getForcedPrice(p, 'Box', 'sales');
                  const fb = getForcedPrice(firstBuyProduct, 'Box', 'sales');
                  const pp = getForcedPrice(p, 'Piece', 'sales');
                  const fp = getForcedPrice(firstBuyProduct, 'Piece', 'sales');
                  priceMatch = Math.abs(pb - fb) < 0.005 && Math.abs(pp - fp) < 0.005;
                } else {
                  priceMatch = true;
                }
                return priceMatch;
              });
            }
            if (nextProducts.length > 0) {
              setProducts(nextProducts);
              setPagination({ ...(nextResp.data.pagination || basePagination), current_page: nextResp.data.pagination?.current_page || nextPage });
              setCurrentPage(nextResp.data.pagination?.current_page || nextPage);
              found = true;
              break;
            }
            nextPage += 1;
          }
          if (!found) {
            setProducts(filteredProducts);
            setPagination(response.data.pagination);
          }
        } else {
          setProducts(filteredProducts);
          setPagination(response.data.pagination);
        }
      } else {
        setError(response.message || 'Failed to load products');
        setProducts([]);
      }
    } catch (error) {
      setError('Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedItems((prev) => {
      const productIdentifier = product.product_id || product.id;
      const isSelected = prev.some((item) => {
        const itemIdentifier = item.product_id || item.id;
        return itemIdentifier === productIdentifier;
      });
      if (isSelected) {
        return prev.filter((item) => {
          const itemIdentifier = item.product_id || item.id;
          return itemIdentifier !== productIdentifier;
        });
      } else {
        // Skip validation for get products (getForSlab)
        if (isGetForSlab) {
          return [...prev, product];
        }

        const baseline = prev[0] || firstBuyProduct;
        if (!productTypeFilter) {
          return [...prev, product];
        }
        if (productTypeFilter === 'box-only' && product.split === 1) {
          showToast('Selected product is piece-only. Please choose products that support boxes.', 'warning');
          return prev;
        }
        if (productTypeFilter === 'piece-only' && product.split === 0) {
          showToast('Selected product is box-only. Please choose products that support pieces.', 'warning');
          return prev;
        }
        if (baseline) {
          const boxMatches = parseNumber(product.salesPrice ?? product.box_sales_price ?? product.price) === parseNumber(baseline.boxSalesPrice ?? baseline.salesPrice ?? baseline.box_sales_price ?? baseline.price);
          const pieceMatches = parseNumber(product.piece_sales_price ?? product.PiecePrice ?? product.price) === parseNumber(baseline.pieceSalesPrice ?? baseline.piece_sales_price ?? baseline.PiecePrice ?? baseline.price);

          if (productTypeFilter === 'both') {
            if (!boxMatches) {
              showToast('Selected product must match the first product\'s box price.', 'warning');
              return prev;
            }
          } else {
            const requireBoxMatch = productTypeFilter !== 'piece-only';
            const requirePieceMatch = productTypeFilter !== 'box-only';
            if ((requireBoxMatch && !boxMatches) || (requirePieceMatch && !pieceMatches)) {
              showToast('Selected product must match price of the first product', 'warning');
              return prev;
            }
          }
        }
        return [...prev, product];
      }
    });
  };

  const handleSelectAll = () => {
    const selectableProducts = products.filter((product) => {
      const productIdentifier = product.product_id || product.id;
      return !alreadyAddedProducts.some((item) => {
        const itemIdentifier = item.product_id || item.id;
        return itemIdentifier === productIdentifier;
      });
    });

    if (selectedItems.length === selectableProducts.length && selectableProducts.length > 0) {
      setSelectedItems([]);
    } else {
      setSelectedItems(selectableProducts);
    }
  };

  const handleConfirm = () => {
    onConfirm(selectedItems);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setCurrentPage(1);
  };

  const handleBrandChange = (event) => {
    setSelectedBrand(event.target.value);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadProducts();
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const selectableProducts = products.filter((product) => {
    const productIdentifier = product.product_id || product.id;
    return !alreadyAddedProducts.some((item) => {
      const itemIdentifier = item.product_id || item.id;
      return itemIdentifier === productIdentifier;
    });
  });

  const isAllSelected =
    selectableProducts.length > 0 &&
    selectableProducts.every((product) => {
      const productIdentifier = product.product_id || product.id;
      return selectedItems.some((item) => {
        const itemIdentifier = item.product_id || item.id;
        return itemIdentifier === productIdentifier;
      });
    });

  const isIndeterminate =
    selectableProducts.some((product) => {
      const productIdentifier = product.product_id || product.id;
      return selectedItems.some((item) => {
        const itemIdentifier = item.product_id || item.id;
        return itemIdentifier === productIdentifier;
      });
    }) && !isAllSelected;

  const totalCount = pagination?.total_count || 0;
  const startIndex = (currentPage - 1) * rowsPerPage + 1;
  const endIndex = Math.min(currentPage * rowsPerPage, totalCount);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ mb: -5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{title}</Typography>
          <Button onClick={onClose} sx={{ minWidth: 'auto', p: 1 }}>
            <Iconify icon="eva:close-fill" />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: '70vh', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 3, pb: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              className="form-input-border"
              size="small"
              fullWidth
              placeholder="Search by product name or product code"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" />
                  </InputAdornment>
                ),
              }}
            />

            <Autocomplete
              className="form-input-border"
              size="small"
              sx={{ minWidth: 250 }}
              options={categories}
              value={selectedCategory}
              onChange={(event, newValue) => {
                handleCategoryChange({ target: { value: newValue || 'All' } });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Category" placeholder="Select Category" />
              )}
              isOptionEqualToValue={(option, value) => option === value}
            />

            <Autocomplete
              className="form-input-border"
              size="small"
              sx={{ minWidth: 250 }}
              options={brands}
              value={selectedBrand}
              onChange={(event, newValue) => {
                handleBrandChange({ target: { value: newValue || 'All' } });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Brand" placeholder="Select Brand" />
              )}
              isOptionEqualToValue={(option, value) => option === value}
            />
          </Box>
        </Box>

        {firstBuyProduct && (
          <Alert
            severity="info"
            sx={{
              mx: 3,
              mb: 2,
              borderRadius: 1,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Filtered by First Product Selection
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Showing only products with matching price
              {productTypeFilter === 'box-only' && ` (Box: £${getForcedPrice(firstBuyProduct, 'Box', 'sales').toFixed(2)})`}
              {productTypeFilter === 'piece-only' && ` (Piece: £${getForcedPrice(firstBuyProduct, 'Piece', 'sales').toFixed(2)})`}
              {productTypeFilter === 'both' && ` (Box: £${getForcedPrice(firstBuyProduct, 'Box', 'sales').toFixed(2)}, Piece: £${getForcedPrice(firstBuyProduct, 'Piece', 'sales').toFixed(2)})`}
            </Typography>
          </Alert>
        )}

        <TableContainer>
          <Table className="table-cell-border"
            size="small"
            sx={{
              tableLayout: "auto",
            }}
          >
            <TableHead>
              <TableRow className="table-cell-header">
                <TableCell padding="checkbox">
                  <Checkbox
                    className="checkBox"
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    disabled={loading || products.length === 0}
                  />
                </TableCell>
                <TableCell width="70px">Code</TableCell>
                <TableCell width="250px" >Name</TableCell>
                <TableCell width="70px">Size</TableCell>
                <TableCell width="80px" align="right">Cost Price</TableCell>
                <TableCell width="80px" align="right">Sales Price</TableCell>
                <TableCell width="80px" align="right">Offer Price</TableCell>
                <TableCell width="60px" align="right">Margin</TableCell>
                <TableCell width="60px" align="right">Stock</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{
                      mt: 2,
                      fontSize: "0.75rem",
           
                    }}>
                      Loading products...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      No products found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => {
                  const productIdentifier = product.product_id || product.id;
                  const isSelected = selectedItems.some((item) => {
                    const itemIdentifier = item.product_id || item.id;
                    return itemIdentifier === productIdentifier;
                  });
                  const isAlreadyAdded = alreadyAddedProducts.some((item) => {
                    const itemIdentifier = item.product_id || item.id;
                    return itemIdentifier === productIdentifier;
                  });
                  return (
                    <TableRow key={product.id} className="table-cell-style">
                      <TableCell padding="checkbox">
                        <Checkbox
                          className="checkBox"
                          checked={isSelected}
                          onChange={() => handleSelectProduct(product)}
                          disabled={isAlreadyAdded}
              

                        />
                      </TableCell>
                      <TableCell>{product.code || product.product_code || product.id}</TableCell>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>{product.size}</TableCell>
                      <TableCell align="right">£{(product.costPrice || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">£{(product.salesPrice || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">£{(product.offerPrice || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">{Math.round(product.margin || 0)}</TableCell>
                      <TableCell align="right">{getProductSelectPopupStock(product)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage - 1}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
            sx={{
              '& .MuiTablePagination-toolbar': {
                px: 2,
                minHeight: 54,
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'flex-start',
                flexWrap: 'nowrap',
              },
              '& .MuiTablePagination-selectLabel': {
                fontSize: '14px',
                color: '#637381',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: '14px',
                color: '#637381',
                margin: 0,
                marginLeft: 'auto',
              },
              '& .MuiTablePagination-select': {
                fontSize: '14px',
                color: '#212B36',
                marginRight: 1,
                marginLeft: 1,
              },
              '& .MuiTablePagination-actions': {
                marginLeft: 1,
              },
              '& .MuiIconButton-root': {
                color: '#637381',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button className="button-variant-outlined" variant="outlined" onClick={onClose}>Cancel</Button>
        <Button
          className="button-variant-contained"
          onClick={handleConfirm}
          variant="contained"
          disabled={selectedItems.length === 0}
          sx={{ backgroundColor: '#003DA5' }}
        >
          Confirm Selection ({selectedItems.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const promotionTypes = [
  {
    id: 'buy-x-get-y-free',
    title: 'Buy X Get Y Free',
    description: 'Buy a specified quantity of items and get items free from another product group.',
  },
  {
    id: 'buy-x-get-y-discount',
    title: 'Buy X Get Y at Discount',
    description: 'Buy specified items and get another product at discounted price or set value.',
  },
  {
    id: 'buy-x-discount',
    title: 'Buy X at Discount...',
    description: 'Buy specified items and get discount on their total value.',
  },
  {
    id: 'multi-layer-offer',
    title: 'Multi-Layer Special Offer',
    description: 'Setup multiple slab-based offers with escalating discounts for same products.',
  },
  {
    id: 'group-offer',
    title: 'Group Offer',
    description: 'Setup multiple slab-based offers with escalating discounts for same products.',
  },
];

const resolveEndDateMinDate = (startDate) => {
  const today = moment().startOf('day');
  if (!startDate) return today;
  const startDay = moment(startDate).startOf('day');
  return moment.max(today, startDay);
};

export default function EditPromotion() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isViewMode = false;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [promotionData, setPromotionData] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [showZeroPriceWarning, setShowZeroPriceWarning] = useState(false);
  const [zeroPriceProducts, setZeroPriceProducts] = useState([]);

  // Form data state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    promotionType: '',
    startDate: null,
    endDate: null,
    selectedChannels: [],
    buyProducts: [],
    getProducts: [],
    slabs: [],
    buyQuantity: 1,
    getQuantity: 1,
    buyGroupQuantities: {},
    getGroupQuantities: {},
    buyPricingType: 'sales',
    getPricingType: 'sales',
    // Buy X at Discount Configuration defaults
    buyXDiscountType: 'no-discount',
    buyXDiscountValue: '',
    buyXDiscountApplyTo: 'none',
    buyDiscountConfigType: 'value-off',
    // Buy X Get Y at Discount Configuration defaults
    getDiscountType: 'set-value',
    getSetValue: '',
    getItemDiscountType: 'percentage',
    getItemDiscountValue: '',
    getItemDiscountApplyTo: 'bulk',
    buyDiscountType: 'percentage',
    buyDiscountValue: '',
    buyDiscountApplyTo: 'none',
  });

  // Channels and other data (default channels matching API structure)
  const [channels, setChannels] = useState([
    { id: '1', value: 'mobile-app', label: 'Customer', bitmap: 1 },
    { id: '3', value: 'sales-rep', label: 'Sales Representative', bitmap: 4 },
    { id: '4', value: 'epos', label: 'EPOS (Cash & Carry)', bitmap: 8 },
    { id: '5', value: 'tele-calling', label: 'Tele Calling', bitmap: 16 },
  ]);
  const [websiteChannelValue, setWebsiteChannelValue] = useState(null);
  const [mobileAppChannelValue, setMobileAppChannelValue] = useState(null);

  // Search states for product selection
  const [buyProductsSearch, setBuyProductsSearch] = useState('');
  const [getProductsSearch, setGetProductsSearch] = useState('');

  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [modalType, setModalType] = useState('buy');
  const [modalTitle, setModalTitle] = useState('');

  // Multi-layer specific states
  const [multiLayerStep, setMultiLayerStep] = useState(1);
  const [currentSlabIndex, setCurrentSlabIndex] = useState(null);

  // Error states
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadChannels();
    loadPromotionData();
  }, [id]);

  useEffect(() => {
    if (formData.promotionType === 'multi-layer-offer' && formData.buyProducts.length > 0 && formData.slabs && formData.slabs.length > 0) {
      setMultiLayerStep(2);
    }
  }, [formData.promotionType, formData.buyProducts, formData.slabs]);

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast({ ...toast, open: false });
  };

  const loadChannels = async () => {
    try {
      const response = await fetchPromotionChannels();

      if (response.success && response.data) {
        const channelsArray = response.data.channels || response.data;

        const websiteChannel = Array.isArray(channelsArray)
          ? channelsArray.find((channel) => channel.is_active && channel.name === 'website')
          : null;
        if (websiteChannel) {
          setWebsiteChannelValue(websiteChannel.name);
        }

        const mobileAppChannel = Array.isArray(channelsArray)
          ? channelsArray.find((channel) => channel.is_active && (channel.name === 'mobile-app' || channel.display_name === 'Mobile Application'))
          : null;
        if (mobileAppChannel) {
          setMobileAppChannelValue(mobileAppChannel.name);
        }

        const channelsData = Array.isArray(channelsArray)
          ? channelsArray
            .filter((channel) => channel.is_active && channel.name !== 'website')
            .map((channel) => ({
              id: channel.id,
              value: channel.name,
              label:
                channel.name === 'mobile-app' || channel.display_name === 'Mobile Application'
                  ? 'Customer'
                  : channel.display_name,
              bitmap: channel.bitmap_value,
              isMobileApp: channel.name === 'mobile-app' || channel.display_name === 'Mobile Application',
            }))
          : [];

        setChannels(channelsData);
      }
    } catch (error) {
    }
  };

  const loadPromotionData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetchPromotionById(id);

      if (response.success && response.data) {
        const data = response.data;
        setPromotionData(data);

        const rawBuyDiscountType =
          (data.buy_discount_type != null && String(data.buy_discount_type).trim() !== '')
            ? String(data.buy_discount_type).trim()
            : (data.buy_discount && data.buy_discount.discount_type != null && String(data.buy_discount.discount_type).trim() !== '')
              ? String(data.buy_discount.discount_type).trim()
              : '';

        const parsedChannels = data.channels
          ? [...new Set(
            data.channels
              .split(', ')
              .flatMap((channel) => {
                if (
                  channel === 'Mobile Application' ||
                  channel === 'Mobile Application (Customer)'
                ) {
                  return ['mobile-app'];
                }

                if (channel === 'Customer') {
                  return ['mobile-app', 'website']; // ✅ key fix
                }

                if (channel === 'Website') return ['website'];
                if (channel === 'Sales Representative') return ['sales-rep'];
                if (channel === 'EPOS (Cash & Carry)') return ['epos'];
                if (channel === 'Tele Calling') return ['tele-calling'];

                return [
                  channel
                    .toLowerCase()
                    .replace(/\s+/g, '-')
                    .replace(/[()]/g, '')
                ];
              })
          )]
          : [];
        // Map promotion type from API to form format
        const mappedPromotionType = data.type
          ? (() => {
            switch (data.type) {
              case 'Buy X Get Y Free':
                return 'buy-x-get-y-free';
              case 'Buy X Get Y at Discount':
                return 'buy-x-get-y-discount';
              case 'Buy X at Discount':
              case 'Buy X at Discount...':
                return 'buy-x-discount';
              case 'Multi-Layer Special Offer':
              case 'Multi Layer Special Offer':
                return 'multi-layer-offer';
              case 'Group Offer':
                return 'group-offer';
              default:
                return data.type.toLowerCase().replace(/\s+/g, '-').replace('...', '');
            }
          })()
          : '';

        const mappedBuyPricingType =
          mappedPromotionType === 'multi-layer-offer'
            ? resolveMultiLayerBuyPricingTypeFromApi(data)
            : data.buy_pricing_type
              ? fromApiPromotionPricingType(data.buy_pricing_type, 'sales')
              : 'sales';

        const mappedGetPricingType =
          mappedPromotionType === 'multi-layer-offer'
            ? resolveMultiLayerGetPricingTypeFromApi(data)
            : data.get_pricing_type
              ? fromApiPromotionPricingType(data.get_pricing_type, 'sales')
              : 'sales';

        // Map buy products with proper structure
        const mappedBuyProducts = (data.buy_products || []).map((product, index) => {
          const normalizedItemDiscountType = normalizeItemDiscountType(product.item_discount_type, { allowNoDiscount: true });
          const resolvedProductType = product.product_type || getDefaultProductType(parseInt(product.split) || 2);
          const pricingFields = resolveBuyProductPricingFields(product, mappedBuyPricingType);
          const pricingTypeForRow =
            resolvedProductType === 'Piece'
              ? pricingFields.piecePricingType
              : pricingFields.boxPricingType;
          return {
            id: product.product_id,
            name: product.product_name,
            code: product.product_code,
            type: resolvedProductType,
            pricingType: pricingTypeForRow,
            boxPricingType: pricingFields.boxPricingType,
            piecePricingType: pricingFields.piecePricingType,
            compulsory: product.is_compulsory === '1',
            quantity: parseInt(product.quantity) || parseInt(product.compulsory_quantity) || 1,
            group_quantity: (() => {
              const raw = product.group_quantity ?? product.groupQuantity ?? product.quantity;
              const n = parseInt(raw, 10);
              return Number.isFinite(n) && n > 0 ? n : 0;
            })(),
            originalIndex: index,
            // Map pricing data directly from API response
            costPrice: parseFloat(product.cost_price) || 0,
            salesPrice: parseFloat(product.sales_price) || 0,
            offerPrice: parseFloat(product.offer_price) || 0,
            boxCostPrice: parseFloat(product.box_cost_price) || 0,
            boxSalesPrice: parseFloat(product.box_sales_price) || 0,
            boxOfferPrice: parseFloat(product.box_offer_price) || 0,
            pieceCostPrice: parseFloat(product.piece_cost_price) || 0,
            pieceSalesPrice: parseFloat(product.piece_sales_price) || 0,
            pieceOfferPrice: parseFloat(product.piece_offer_price) || 0,
            split: parseInt(product.split) || 2,
            pack_size: parseInt(product.pack) || 1,
            ...mapPromotionApiStockFields(product),
            // Add default size if not available
            size: product.size || 'N/A',
            // Map discount fields for Buy X at Discount
            buyXItemDiscountType: normalizedItemDiscountType,
            buyXItemDiscountValue: product.item_discount_value || '',
            // Map discount fields for Buy X Get Y at Discount (buy products)
            buyItemDiscountType: normalizeItemDiscountType(product.item_discount_type) || 'percentage',
            buyItemDiscountValue: product.item_discount_value || '',
            groupNo:
              product.group_id != null && String(product.group_id).trim() !== ''
                ? String(product.group_id)
                : product.group_no != null && String(product.group_no).trim() !== ''
                  ? String(product.group_no)
                  : '',
          };
        });

        // Map get products with proper structure
        const mappedGetProducts = (data.get_products || []).map((product, index) => {
          const getLevelFallback = mappedGetPricingType;
          const resolvedProductType = product.product_type || getDefaultProductType(parseInt(product.split) || 2);
          const boxPricingNorm = normalizePieceBoxPricingSelect(product.box_pricing_type, getLevelFallback);
          const piecePricingNorm = normalizePieceBoxPricingSelect(product.piece_pricing_type, getLevelFallback);
          const pricingTypeForRow =
            resolvedProductType === 'Piece'
              ? piecePricingNorm
              : resolvedProductType === 'Box'
                ? boxPricingNorm
                : boxPricingNorm;
          return {
            id: product.product_id,
            name: product.product_name,
            code: product.product_code,
            type: resolvedProductType,
            pricingType: pricingTypeForRow,
            boxPricingType: boxPricingNorm,
            piecePricingType: piecePricingNorm,
            compulsory: product.is_compulsory === '1' || product.is_compulsory === 1 || product.is_compulsory === true,
            quantity: parseInt(product.quantity) || parseInt(product.compulsory_quantity) || 1,
            group_quantity: (() => {
              const raw = product.group_quantity ?? product.groupQuantity ?? product.quantity;
              const n = parseInt(raw, 10);
              return Number.isFinite(n) && n > 0 ? n : 0;
            })(),
            originalIndex: index,
            // Map pricing data directly from API response
            costPrice: parseFloat(product.cost_price) || 0,
            salesPrice: parseFloat(product.sales_price) || 0,
            offerPrice: parseFloat(product.offer_price) || 0,
            boxCostPrice: parseFloat(product.box_cost_price) || 0,
            boxSalesPrice: parseFloat(product.box_sales_price) || 0,
            boxOfferPrice: parseFloat(product.box_offer_price) || 0,
            pieceCostPrice: parseFloat(product.piece_cost_price) || 0,
            pieceSalesPrice: parseFloat(product.piece_sales_price) || 0,
            pieceOfferPrice: parseFloat(product.piece_offer_price) || 0,
            split: parseInt(product.split) || 2,
            pack_size: parseInt(product.pack) || 1,
            ...mapPromotionApiStockFields(product),
            // Add default size if not available
            size: product.size || 'N/A',
            // Map discount fields for Buy X Get Y at Discount (get products)
            itemDiscountType: normalizeItemDiscountType(product.item_discount_type) || 'percentage',
            itemDiscountValue: product.item_discount_value || '',
            groupNo:
              product.group_id != null && String(product.group_id).trim() !== ''
                ? String(product.group_id)
                : product.group_no != null && String(product.group_no).trim() !== ''
                  ? String(product.group_no)
                  : '',
          };
        });

        let finalBuyPricingType = mappedBuyPricingType;
        if (
          mappedPromotionType === 'multi-layer-offer' &&
          mappedBuyProducts.some((product) => product.boxPricingType === 'offer' || product.pricingType === 'offer')
        ) {
          finalBuyPricingType = 'offer';
        }

        // Determine buyProductType for multi-layer offers based on product types
        let buyProductType = 'box-only';
        if (mappedPromotionType === 'multi-layer-offer' && mappedBuyProducts.length > 0) {
          const hasBox = mappedBuyProducts.some(p => p.type === 'Box' || p.type === 'Both');
          const hasPiece = mappedBuyProducts.some(p => p.type === 'Piece' || p.type === 'Both');
          if (hasBox && !hasPiece) buyProductType = 'box-only';
          else if (hasPiece && !hasBox) buyProductType = 'piece-only';
          else buyProductType = 'both';
        }

        // Map slabs data for multi-layer offers
        const mappedSlabs = (data.slabs || []).map((slab, index) => {
          // Map discount type from API format to form format
          let discountType = '';
          const apiDiscountType = slab.box_discount_type || slab.piece_discount_type || '';

          if (apiDiscountType) {
            if (apiDiscountType.toLowerCase() === 'set_value' || apiDiscountType.toLowerCase() === 'set-value') {
              discountType = 'set-value';
            } else if (apiDiscountType.toLowerCase() === 'percentage' || apiDiscountType.toLowerCase() === 'percent') {
              discountType = 'percentage';
            } else if (apiDiscountType.toLowerCase() === 'fixed' || apiDiscountType.toLowerCase() === 'fixed_amount') {
              discountType = 'fixed';
            } else {
              discountType = apiDiscountType;
            }
          }

          // Map get products for this slab
          const mappedGetProducts = (slab.get_products || []).map((product, pIndex) => {
            const getLevelFallback = mappedGetPricingType;
            let resolvedType = product.product_type;
            if (!resolvedType || resolvedType === 'GET') {
              if (buyProductType === 'box-only') {
                resolvedType = 'Box';
              } else if (buyProductType === 'piece-only') {
                resolvedType = 'Piece';
              } else {
                resolvedType = 'Box';
              }
            } else {
              resolvedType = product.product_type || getDefaultProductType(parseInt(product.split) || 2);
            }
            const boxPricingNorm = normalizePieceBoxPricingSelect(
              product.box_pricing_type,
              getLevelFallback
            );
            const piecePricingNorm = normalizePieceBoxPricingSelect(
              product.piece_pricing_type,
              getLevelFallback
            );
            const pricingTypeForRow =
              resolvedType === 'Piece' ? piecePricingNorm : boxPricingNorm;

            return {
            id: product.product_id,
            name: product.product_name,
            code: product.product_code,
            type: resolvedType,
            pricingType: pricingTypeForRow,
            boxPricingType: boxPricingNorm,
            piecePricingType: piecePricingNorm,
            quantity: parseInt(product.quantity) || parseInt(product.compulsory_quantity) || 1,
            compulsory: product.is_compulsory === '1' || product.is_compulsory === 1 || product.is_compulsory === true,
            originalIndex: pIndex,
            costPrice: parseFloat(product.cost) || 0,
            salesPrice: parseFloat(product.box_price) || 0,
            offerPrice: parseFloat(product.box_offer_price) || 0,
            boxCostPrice: parseFloat(product.cost) || 0,
            boxSalesPrice: parseFloat(product.box_price) || 0,
            boxOfferPrice: parseFloat(product.box_offer_price) || 0,
            pieceCostPrice: parseFloat(product.cost) || 0,
            pieceSalesPrice: parseFloat(product.piece_price) || 0,
            pieceOfferPrice: parseFloat(product.piece_offer_price) || 0,
            split: parseInt(product.split) || 2,
            pack_size: parseInt(product.pack) || 1,
            ...mapPromotionApiStockFields(product),
            size: product.size || 'N/A',
          };
          });

          return {
            id: slab.slab_id || Date.now() + index,
            slabOrder: parseInt(slab.slab_order) || index + 1,
            slabName: slab.slab_name || `Slab ${index + 1}`,
            productType: buyProductType,
            boxMinQuantity: slab.box_min_quantity || '',
            boxMaxQuantity: slab.box_max_quantity || '',
            pieceMinQuantity: slab.piece_min_quantity || '',
            pieceMaxQuantity: slab.piece_max_quantity || '',
            discountType,
            boxProductsValue: slab.box_discount_value || '',
            pieceProductsValue: slab.piece_discount_value || '',
            hasGetProducts: slab.has_get_products === '1' || slab.has_get_products === 1,
            getQuantity: slab.get_quantity != null && slab.get_quantity !== '' ? parseInt(slab.get_quantity) : '',
            getProducts: mappedGetProducts,
          };
        });

        // Map discount configuration for Buy X Get Y at Discount
        const buyDiscountEnabled = data.buy_discount_enabled === true || data.buy_discount_enabled === '1';
        const getDiscountEnabled = data.get_discount_enabled === true || data.get_discount_enabled === '1';

        let buyDiscountType = 'percentage';
        let buyDiscountApplyTo = 'none';
        if (buyDiscountEnabled && rawBuyDiscountType) {
          const discountType = normalizeDiscountType(rawBuyDiscountType);
          if (discountType === 'percentage') buyDiscountType = 'percentage';
          else if (discountType === 'fixed') buyDiscountType = 'fixed';
          else if (discountType === 'set-value') buyDiscountType = 'set-value';
        }

        const discountMethod = (data.buy_discount_method || '').toUpperCase();
        if (discountMethod === 'ITEM_WISE') buyDiscountApplyTo = 'per-item';
        else if (discountMethod === 'BULK_ASSIGN') buyDiscountApplyTo = 'bulk';

        // Map get discount type
        let getDiscountType = 'set-value';
        let getItemDiscountApplyTo = 'bulk';
        if (getDiscountEnabled && data.get_discount_type) {
          const discountType = data.get_discount_type.toLowerCase();
          if (discountType === 'set_value' || discountType === 'set-value') {
            getDiscountType = 'set-value';
          } else {
            getDiscountType = 'value-off';
            const discountMethod = (data.get_discount_method || '').toUpperCase();
            if (discountMethod === 'ITEM_WISE') getItemDiscountApplyTo = 'per-item';
            else if (discountMethod === 'BULK_ASSIGN') getItemDiscountApplyTo = 'bulk';
            else getItemDiscountApplyTo = 'bulk';
          }
        }

        const discountMode = (data.buy_discount_mode || data.buy_discount_config_type || '').toUpperCase();
        const normalizedBuyDiscountType = normalizeDiscountType(rawBuyDiscountType);
        const resolvedBuyDiscountConfigType = discountMode === 'SET_VALUE' ? 'set-value' : 'value-off';
        const resolvedBuyXDiscountType = (() => {
          if (resolvedBuyDiscountConfigType !== 'value-off') return 'no-discount';
          if (normalizedBuyDiscountType === 'percentage') return 'percentage';
          if (normalizedBuyDiscountType === 'fixed') return 'fixed';
          if (normalizedBuyDiscountType === 'set-value') return 'no-discount';
          return 'percentage';
        })();
        const resolvedBuyXDiscountApplyTo = resolvedBuyDiscountConfigType === 'value-off'
          ? (data.buy_discount_method === 'ITEM_WISE' ? 'per-item' : 'bulk')
          : 'bulk';


        const buildGroupQuantitiesFromProducts = (products) => {
          const map = {};
          (products || []).forEach((product) => {
            const g =
              product.groupNo != null && String(product.groupNo).trim() !== ""
                ? String(product.groupNo).trim()
                : "";
            if (!g) return;
            // Prefer API group_quantity; 0 / missing stays empty (optional).
            const raw = product.group_quantity ?? product.groupQuantity ?? product.quantity;
            const q = parseInt(raw, 10);
            if (!Number.isFinite(q) || q <= 0) {
              if (map[g] === undefined) map[g] = '';
              return;
            }
            map[g] = String(Math.max(parseInt(map[g] || '0', 10) || 0, q));
          });
          return map;
        };
        const buyGroupQuantities = buildGroupQuantitiesFromProducts(mappedBuyProducts);
        const getGroupQuantities = buildGroupQuantitiesFromProducts(mappedGetProducts);

        // Map API data to form data
        setFormData({
          title: data.title || '',
          description: data.description || '',
          promotionType: mappedPromotionType,
          startDate: data.start_date_raw ? moment(data.start_date_raw) : null,
          endDate: data.end_date_raw ? moment(data.end_date_raw) : null,
          selectedChannels: parsedChannels,
          buyProducts: mappedBuyProducts,
          getProducts: mappedGetProducts,
          buyProductType,
          slabs: mappedSlabs,
          // Add promotion-level quantities and pricing types
          buyQuantity: parseInt(data.buy_quantity) || 1,
          getQuantity: parseInt(data.get_quantity) || 1,
          buyGroupQuantities,
          getGroupQuantities,
          buyPricingType: finalBuyPricingType,
          getPricingType: mappedGetPricingType,
          // Map Buy X at Discount configuration
          buyXDiscountType: resolvedBuyXDiscountType,
          buyXDiscountValue: resolvedBuyDiscountConfigType === 'value-off' ? (data.buy_discount_value || '') : '',
          buyXDiscountApplyTo: resolvedBuyXDiscountApplyTo,
          buyDiscountConfigType: resolvedBuyDiscountConfigType,
          buySetValue: resolvedBuyDiscountConfigType === 'set-value' ? (data.buy_discount_value || '') : '',
          // Map Buy X Get Y at Discount configuration
          buyDiscountType,
          buyDiscountValue: data.buy_discount_value || '',
          buyDiscountApplyTo,
          getDiscountType,
          getSetValue: getDiscountType === 'set-value' ? (data.get_discount_value || '') : '',
          getItemDiscountType: getDiscountType === 'value-off' ? (data.get_discount_type === 'PERCENTAGE' ? 'percentage' : 'fixed') : 'percentage',
          getItemDiscountValue: getDiscountType === 'value-off' ? (data.get_discount_value || '') : '',
          getItemDiscountApplyTo,
        });
      } else {
        setError(response.message || 'Failed to load promotion data');
      }
    } catch (error) {
      setError('Failed to load promotion data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'buyPricingType') {
      const pricingType = value === 'offer' ? 'offer' : 'sales';
      setFormData((prev) => ({
        ...prev,
        buyPricingType: pricingType,
        buyProducts: (prev.buyProducts || []).map((product) => ({
          ...product,
          pricingType,
          boxPricingType: pricingType,
          piecePricingType: pricingType,
        })),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (field === 'slabs') {
      setErrors((prev) => applySlabGetQuantityCompulsoryErrors(prev, value));
    }

    // Clear stale validation as the user fixes fields (error keys do not always match field names).
    if (
      field === 'buyGroupQuantities' ||
      field === 'getGroupQuantities' ||
      field === 'buyQuantity' ||
      field === 'getQuantity'
    ) {
      const errorPrefix =
        field === 'buyGroupQuantities' || field === 'buyQuantity'
          ? 'buyGroupQty'
          : 'getGroupQty';
      setErrors((prev) => {
        let changed = false;
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key === errorPrefix || key.startsWith(`${errorPrefix}_`)) {
            if (next[key]) {
              delete next[key];
              changed = true;
            }
          }
        });
        if (field === 'buyQuantity') {
          if (next.buyQuantity) {
            delete next.buyQuantity;
            changed = true;
          }
          if (next.compulsoryQuantity) {
            delete next.compulsoryQuantity;
            changed = true;
          }
        }
        if (field === 'getQuantity' && next.getQuantity) {
          delete next.getQuantity;
          changed = true;
        }
        return changed ? next : prev;
      });
    } else if (field === 'buyProducts' || field === 'getProducts') {
      const prefix = field === 'buyProducts' ? 'buyProduct' : 'getProduct';
      setErrors((prev) => {
        let changed = false;
        const next = { ...prev };
        if (next[field]) {
          delete next[field];
          changed = true;
        }
        if (field === 'buyProducts' && next.compulsoryProducts) {
          delete next.compulsoryProducts;
          changed = true;
        }
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${prefix}_`)) {
            delete next[key];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    } else if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleChannelToggle = (channelValue) => {
    const channel = channels.find((ch) => ch.value === channelValue);
    const isMobileApp = channel?.isMobileApp || false;

    setFormData((prev) => {
      if (isMobileApp) {
        const hasMobileApp = mobileAppChannelValue && prev.selectedChannels.includes(mobileAppChannelValue);
        if (hasMobileApp) {
          const newChannels = prev.selectedChannels.filter((c) => c !== mobileAppChannelValue);
          if (websiteChannelValue && newChannels.includes(websiteChannelValue)) {
            return {
              ...prev,
              selectedChannels: newChannels.filter((c) => c !== websiteChannelValue),
            };
          }
          return { ...prev, selectedChannels: newChannels };
        }
        const newChannels = [...prev.selectedChannels];
        if (mobileAppChannelValue && !newChannels.includes(mobileAppChannelValue)) newChannels.push(mobileAppChannelValue);
        if (websiteChannelValue && !newChannels.includes(websiteChannelValue)) newChannels.push(websiteChannelValue);
        return { ...prev, selectedChannels: newChannels };
      }
      return {
        ...prev,
        selectedChannels: prev.selectedChannels.includes(channelValue)
          ? prev.selectedChannels.filter((c) => c !== channelValue)
          : [...prev.selectedChannels, channelValue],
      };
    });

    setErrors((prev) => {
      if (!prev.channels) return prev;
      const next = { ...prev };
      delete next.channels;
      return next;
    });
  };

  const handleProductTypeChange = (productId, type, newType) => {
    const listKey = type === 'buy' ? 'buyProducts' : 'getProducts';
    const prefix = type === 'buy' ? 'buyProduct' : 'getProduct';
    const productIndex = findPromotionProductListIndex(formData[listKey], productId);

    setFormData((prev) => ({
      ...prev,
      [listKey]: prev[listKey].map((product, index) => {
        if (`${product.id}-${index}` === productId) {
          return { ...product, type: newType || getDefaultProductType(product.split) };
        }
        const currentType = product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null'
          ? product.type
          : getDefaultProductType(product.split);
        return { ...product, type: currentType };
      }),
    }));

    if (productIndex >= 0) {
      setErrors((prev) => clearProductErrorKeys(prev, productIndex, prefix, ['type']));
    }
  };

  const handleProductPricingChange = (productId, type, pricingType) => {
    const listKey = type === 'buy' ? 'buyProducts' : 'getProducts';
    const prefix = type === 'buy' ? 'buyProduct' : 'getProduct';
    const productIndex = findPromotionProductListIndex(formData[listKey], productId);

    setFormData((prev) => ({
      ...prev,
      [listKey]: prev[listKey].map((product, index) => {
        if (`${product.id}-${index}` !== productId) return product;
        const pt =
          product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null'
            ? product.type
            : getDefaultProductType(product.split);
        const next = { ...product, pricingType };
        if (pt === 'Piece') return { ...next, piecePricingType: pricingType };
        if (pt === 'Box') return { ...next, boxPricingType: pricingType };
        if (pt === 'Both') return { ...next, boxPricingType: pricingType, piecePricingType: pricingType };
        return next;
      }),
    }));

    if (productIndex >= 0) {
      setErrors((prev) =>
        clearProductErrorKeys(prev, productIndex, prefix, ['pricing', 'offerPrice'])
      );
    }
  };

  const handleRemoveProduct = (productId, type) => {
    const listKey = type === 'buy' ? 'buyProducts' : 'getProducts';
    const prefix = type === 'buy' ? 'buyProduct' : 'getProduct';
    const removedIndex = findPromotionProductListIndex(formData[listKey], productId);

    setFormData((prev) => ({
      ...prev,
      [listKey]: prev[listKey].filter((p, index) => `${p.id}-${index}` !== productId),
    }));

    if (removedIndex >= 0) {
      setErrors((prev) => shiftProductErrorsAfterRemove(prev, removedIndex, prefix));
    }
  };

  const handleProductQuantityChange = (productId, type, quantity) => {
    setFormData((prev) => ({
      ...prev,
      [type === 'buy' ? 'buyProducts' : 'getProducts']: prev[
        type === 'buy' ? 'buyProducts' : 'getProducts'
      ].map((product, index) =>
        `${product.id}-${index}` === productId
          ? { ...product, quantity: quantity === '' ? '' : (parseInt(quantity) || 1) }
          : product
      ),
    }));
  };

  const handleProductCompulsoryChange = (productId, type, compulsory) => {
    setFormData((prev) => ({
      ...prev,
      [type === 'buy' ? 'buyProducts' : 'getProducts']: prev[
        type === 'buy' ? 'buyProducts' : 'getProducts'
      ].map((product, index) =>
        `${product.id}-${index}` === productId
          ? {
            ...product,
            compulsory,
            quantity: compulsory && (!product.quantity || product.quantity === '') ? 1 : product.quantity
          }
          : product
      ),
    }));
  };

  const handleProductItemDiscountTypeChange = (productId, discountType) => {
    setFormData((prev) => ({
      ...prev,
      getProducts: prev.getProducts.map((product, index) =>
        `${product.id}-${index}` === productId
          ? { ...product, itemDiscountType: discountType }
          : product
      ),
    }));
  };

  const handleProductItemDiscountValueChange = (productId, discountValue) => {
    setFormData((prev) => ({
      ...prev,
      getProducts: prev.getProducts.map((product, index) =>
        `${product.id}-${index}` === productId
          ? { ...product, itemDiscountValue: discountValue }
          : product
      ),
    }));
  };

  const handleSlabProductTypeChange = (slabIndex, productId, newType) => {
    const newSlabs = [...(formData.slabs || [])];
    newSlabs[slabIndex] = {
      ...newSlabs[slabIndex],
      getProducts: (newSlabs[slabIndex].getProducts || []).map((product, index) =>
        `${product.id}-${index}` === productId ? { ...product, type: newType } : product
      ),
    };
    setFormData((prev) => ({ ...prev, slabs: newSlabs }));
  };

  const handleSlabProductPricingChange = (slabIndex, productId, field, value) => {
    const newSlabs = [...(formData.slabs || [])];
    newSlabs[slabIndex] = {
      ...newSlabs[slabIndex],
      getProducts: (newSlabs[slabIndex].getProducts || []).map((product, index) => {
        if (`${product.id}-${index}` !== productId) return product;
        const pt =
          product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null'
            ? product.type
            : getDefaultProductType(product.split);
        const next = { ...product, [field]: value };
        if (field !== 'pricingType') return next;
        if (pt === 'Piece') return { ...next, piecePricingType: value };
        if (pt === 'Box') return { ...next, boxPricingType: value };
        if (pt === 'Both') return { ...next, boxPricingType: value, piecePricingType: value };
        return next;
      }),
    };
    setFormData((prev) => ({ ...prev, slabs: newSlabs }));
  };

  const handleSlabProductQuantityChange = (slabIndex, productId, quantity) => {
    setFormData((prev) => {
      const newSlabs = [...(prev.slabs || [])];
      newSlabs[slabIndex] = {
        ...newSlabs[slabIndex],
        getProducts: (newSlabs[slabIndex].getProducts || []).map((product, index) =>
          `${product.id}-${index}` === productId
            ? { ...product, quantity: quantity === '' ? '' : (parseInt(quantity) || 1) }
            : product
        ),
      };
      setErrors((prevErrors) => applySlabGetQuantityCompulsoryErrors(prevErrors, newSlabs));
      return { ...prev, slabs: newSlabs };
    });
  };

  const handleSlabProductCompulsoryChange = (slabIndex, productId, checked) => {
    setFormData((prev) => {
      const newSlabs = [...(prev.slabs || [])];
      newSlabs[slabIndex] = {
        ...newSlabs[slabIndex],
        getProducts: (newSlabs[slabIndex].getProducts || []).map((product, index) =>
          `${product.id}-${index}` === productId
            ? {
              ...product,
              compulsory: checked,
              quantity: checked && (!product.quantity || product.quantity === '') ? 1 : product.quantity
            }
            : product
        ),
      };
      setErrors((prevErrors) => applySlabGetQuantityCompulsoryErrors(prevErrors, newSlabs));
      return { ...prev, slabs: newSlabs };
    });
  };

  const handleSlabRemoveProduct = (slabIndex, productId) => {
    setFormData((prev) => {
      const newSlabs = [...(prev.slabs || [])];
      newSlabs[slabIndex] = {
        ...newSlabs[slabIndex],
        getProducts: (newSlabs[slabIndex].getProducts || []).filter(
          (p, index) => `${p.id}-${index}` !== productId
        ),
      };
      setErrors((prevErrors) => applySlabGetQuantityCompulsoryErrors(prevErrors, newSlabs));
      return { ...prev, slabs: newSlabs };
    });
  };

  const handleOpenProductModal = (type) => {
    setModalType(type);
    setModalTitle(type === 'buy' ? 'Select Buy Products' : 'Select Get Products');
    setShowProductModal(true);
  };

  const handleCloseProductModal = () => {
    setShowProductModal(false);
  };

  const getDefaultTypeForMultiLayer = (product) => {
    if (formData.promotionType === 'multi-layer-offer') {
      const buyProductType = formData.buyProductType || 'box-only';

      // For get products (getForSlab)
      if (modalType === 'getForSlab') {
        if (buyProductType === 'box-only') return 'Box';
        if (buyProductType === 'piece-only') return 'Piece';
        if (buyProductType === 'both') return 'Box'; // Always default to Box for 'both' in get products
      }

      // For buy products
      if (modalType === 'buy') {
        if (buyProductType === 'box-only') return 'Box';
        if (buyProductType === 'piece-only') return 'Piece';
        if (buyProductType === 'both') {
          if (product.split === 0) return 'Box';
          if (product.split === 1) return 'Piece';
          if (product.split === 2) return 'Both';
        }
      }
    }
    const hasValidType = product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null';
    return hasValidType ? product.type : getDefaultProductType(product.split);
  };

  const handleConfirmProducts = (selectedProducts) => {
    const buildProductsWithType = (prevFormData) => {
      const isMultiLayerBuy =
        prevFormData.promotionType === 'multi-layer-offer' && modalType === 'buy';
      const buyPricingType = prevFormData.buyPricingType === 'offer' ? 'offer' : 'sales';

      return selectedProducts.map((product, index) => {
        const productId = product.product_id || product.id;
        const productCode = product.product_code || product.code;
        const resolvedPricing = isMultiLayerBuy
          ? buyPricingType
          : product.pricingType || 'sales';

        return {
          ...product,
          id: productId,
          code: productCode || productId,
          type: getDefaultTypeForMultiLayer(product),
          pricingType: resolvedPricing,
          boxPricingType: isMultiLayerBuy
            ? buyPricingType
            : product.boxPricingType || product.pricingType || 'sales',
          piecePricingType: isMultiLayerBuy
            ? buyPricingType
            : product.piecePricingType || product.pricingType || 'sales',
          compulsory: product.compulsory || false,
          quantity: product.quantity || 1,
          originalIndex: index,
          buyXItemDiscountType: product.buyXItemDiscountType || 'no-discount',
          buyXItemDiscountValue: product.buyXItemDiscountValue || '',
        };
      });
    };

    if (modalType === 'getForSlab' && currentSlabIndex !== null) {
      setFormData((prev) => {
        const productsWithType = buildProductsWithType(prev);
        const newSlabs = [...(prev.slabs || [])];
        newSlabs[currentSlabIndex] = {
          ...newSlabs[currentSlabIndex],
          getProducts: productsWithType,
        };
        setErrors((prevErrors) => applySlabGetQuantityCompulsoryErrors(prevErrors, newSlabs));
        return {
          ...prev,
          slabs: newSlabs,
        };
      });
      setCurrentSlabIndex(null);
    } else {
      const prefix = modalType === 'buy' ? 'buyProduct' : 'getProduct';
      setFormData((prev) => ({
        ...prev,
        [modalType === 'buy' ? 'buyProducts' : 'getProducts']: buildProductsWithType(prev),
      }));
      setErrors((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${prefix}_`)) {
            delete next[key];
          }
        });
        return next;
      });
    }
    setShowProductModal(false);
  };

  const getDefaultProductType = (split) => {
    if (split === 0) return 'Box';
    if (split === 1) return 'Piece';
    return 'Box';
  };

  useEffect(() => {
    if (formData.promotionType !== 'multi-layer-offer') {
      return;
    }
    setErrors((prev) => {
      const newErrors = { ...prev };
      let changed = false;
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith('buyProduct_') && key.includes('_packSize')) {
          delete newErrors[key];
          changed = true;
        }
      });
      return changed ? newErrors : prev;
    });
  }, [formData.buyProducts, formData.promotionType]);

  const validateBuyXGetYFree = () => {
    const errors = {};

    if (formData.buyProducts.length === 0) {
      errors.buyProducts = 'Please add at least one buy product';
    } else if (formData.buyProducts.length > 200) {
      errors.buyProducts = `You cannot add more than 200 products. Current count: ${formData.buyProducts.length}`;
    }

    formData.buyProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`buyProduct_${index}_type`] = 'Product Type is necessary';
      }
      if (!product.pricingType) {
        errors[`buyProduct_${index}_pricing`] = 'Pricing Type is necessary';
      }
      applyZeroPriceProductError(errors, product, index, 'buyProduct');
      if (product.compulsory && (!product.quantity || product.quantity === '' || parseInt(product.quantity) < 1)) {
        errors[`buyProduct_${index}_quantity`] = 'Quantity is required for compulsory products';
      }
    });

    if (!formData.buyQuantity || formData.buyQuantity === '') {
      errors.buyQuantity = 'Buy quantity is required and must be a positive number';
    } else {
      const buyQty = parseInt(formData.buyQuantity);
      if (isNaN(buyQty) || buyQty < 1) {
        errors.buyQuantity = 'Buy quantity must be at least 1';
      } else if (buyQty > 9999) {
        errors.buyQuantity = 'Buy quantity cannot exceed 9999';
      } else {
        const compulsoryCount = formData.buyProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (p.quantity || 1), 0);
        if (compulsoryCount > buyQty) {
          errors.buyQuantity = `Compulsory quantity (${compulsoryCount}) cannot exceed buy quantity (${buyQty})`;
        }
      }
    }

    if (formData.getProducts.length === 0) {
      errors.getProducts = 'Please add at least one get product';
    } else if (formData.getProducts.length > 200) {
      errors.getProducts = 'You cannot add more than 200 products to get list';
    }

    formData.getProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`getProduct_${index}_type`] = 'Product Type is necessary';
      }
      if (!product.pricingType) {
        errors[`getProduct_${index}_pricing`] = 'Pricing Type is necessary';
      }
      if (product.compulsory && (!product.quantity || product.quantity === '' || parseInt(product.quantity) < 1)) {
        errors[`getProduct_${index}_quantity`] = 'Quantity is required for compulsory products';
      }
    });

    if (!formData.getQuantity || formData.getQuantity === '') {
      errors.getQuantity = 'Get quantity is required and must be a positive number';
    } else {
      const getQty = parseInt(formData.getQuantity);
      if (isNaN(getQty) || getQty < 1) {
        errors.getQuantity = 'Get quantity must be at least 1';
      } else if (getQty > 9999) {
        errors.getQuantity = 'Get quantity cannot exceed 9999';
      } else {
        const compulsoryCount = formData.getProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (p.quantity || 1), 0);
        if (compulsoryCount > getQty) {
          errors.getQuantity = `Compulsory quantity (${compulsoryCount}) cannot exceed get quantity (${getQty})`;
        }
      }
    }

    return errors;
  };

  const getNormalizedProductType = (product) => {
    if (product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null') {
      return product.type;
    }
    if (product.split === 1) return 'Piece';
    return 'Box';
  };

  const getProductUnitPrice = (product) => {
    const type = getNormalizedProductType(product);
    const basePt =
      product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null'
        ? product.pricingType
        : 'sales';
    let pricingMode = basePt;
    if (type === 'Piece') {
      const piecePt =
        product.piecePricingType && product.piecePricingType !== '' && product.piecePricingType !== 'undefined' && product.piecePricingType !== 'null'
          ? product.piecePricingType
          : basePt;
      pricingMode = piecePt;
    } else if (type === 'Box') {
      const boxPt =
        product.boxPricingType && product.boxPricingType !== '' && product.boxPricingType !== 'undefined' && product.boxPricingType !== 'null'
          ? product.boxPricingType
          : basePt;
      pricingMode = boxPt;
    }

    const pickPrice = (...values) => {
      for (const value of values) {
        const parsed = parseFloat(value);
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }
      return 0;
    };

    if (type === 'Piece') {
      if (pricingMode === 'offer') {
        return pickPrice(product.pieceOfferPrice, product.offerPiecePrice, product.offerPrice, product.price);
      }
      return pickPrice(product.pieceSalesPrice, product.piece_sales_price, product.PiecePrice, product.salesPrice, product.price);
    }

    if (pricingMode === 'offer') {
      return pickPrice(product.boxOfferPrice, product.offerBoxPrice, product.offerPrice);
    }
    return pickPrice(product.boxSalesPrice, product.salesPrice, product.price);
  };

  const getProductTotalValue = (product) => {
    const quantity = parseInt(product.quantity, 10);
    const normalizedQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    return getProductUnitPrice(product) * normalizedQuantity;
  };

  const getProductsTotalValue = (products = []) =>
    products.reduce((sum, product) => sum + getProductTotalValue(product), 0);

  const getProductsBulkTotalValue = (products = []) =>
    products.reduce((sum, product) => sum + getProductUnitPrice(product), 0);

  const getProductErrorIndex = (product, index) =>
    product.originalIndex !== undefined ? product.originalIndex : index;

  const getProductDisplayLabel = (product) => {
    const name = product.name || product.product_name || 'Product';
    const code = product.code || product.product_code || product.id;
    return code ? `${name} (${code})` : name;
  };

  const applyZeroPriceProductError = (errors, product, index, keyPrefix = 'buyProduct') => {
    if (!product.pricingType) return;
    const productKey = getProductErrorIndex(product, index);
    const unitPrice = getProductUnitPrice(product);
    if (unitPrice !== 0) return;

    const pricingLabel = product.pricingType === 'offer' ? 'Offer' : 'Standard';
    const message = `${getProductDisplayLabel(product)}: ${pricingLabel} price is 0.00. Please select a valid price.`;
    const errorKey =
      product.pricingType === 'offer'
        ? `${keyPrefix}_${productKey}_offerPrice`
        : `${keyPrefix}_${productKey}_pricing`;
    errors[errorKey] = message;
  };

  const applyMultiLayerBuyZeroPriceErrors = (errors, product, index) => {
    const productKey = getProductErrorIndex(product, index);
    const buyProductType = formData.buyProductType || 'box-only';
    const buyPricingType = formData.buyPricingType || 'sales';
    const productType = product.type || 'Box';
    const label = getProductDisplayLabel(product);

    const setZeroError = (priceKind, errorKey, mode) => {
      const price = getForcedPrice(product, priceKind, mode);
      if (price === 0) {
        const pricingLabel = mode === 'offer' ? 'Offer' : 'Standard';
        errors[errorKey] = `${label}: ${priceKind} ${pricingLabel} price is 0.00. Please select a valid price.`;
      }
    };

    if ((buyProductType === 'box-only' || buyProductType === 'both') && (productType === 'Box' || productType === 'Both')) {
      const mode = getMultiLayerBuyPricingMode(product, 'Box', buyPricingType);
      setZeroError('Box', `buyProduct_${productKey}_boxPrice`, mode);
    }
    if ((buyProductType === 'piece-only' || buyProductType === 'both') && (productType === 'Piece' || productType === 'Both')) {
      const mode = getMultiLayerBuyPricingMode(product, 'Piece', buyPricingType);
      setZeroError('Piece', `buyProduct_${productKey}_piecePrice`, mode);
    }
  };

  const validateBuyXGetYDiscount = () => {
    const errors = {};

    if (formData.buyProducts.length === 0) {
      errors.buyProducts = 'Please add at least one buy product';
    } else if (formData.buyProducts.length > 200) {
      errors.buyProducts = `You cannot add more than 200 products. Current count: ${formData.buyProducts.length}`;
    }

    formData.buyProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`buyProduct_${index}_type`] = 'Product Type is necessary';
      }
      if (!product.pricingType) {
        errors[`buyProduct_${index}_pricing`] = 'Please select price type (Sales Price or Offer Price)';
      }
      applyZeroPriceProductError(errors, product, index, 'buyProduct');
    });

    if (!formData.buyQuantity || formData.buyQuantity === '') {
      errors.buyQuantity = 'Buy quantity is required and must be a positive number';
    } else {
      const buyQty = parseInt(formData.buyQuantity);
      if (isNaN(buyQty) || buyQty < 1) {
        errors.buyQuantity = 'Buy quantity must be at least 1';
      } else if (buyQty > 9999) {
        errors.buyQuantity = 'Buy quantity cannot exceed 9999';
      } else {
        const compulsoryCount = formData.buyProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (p.quantity || 1), 0);
        if (compulsoryCount > buyQty) {
          errors.buyQuantity = `Compulsory quantity (${compulsoryCount}) cannot exceed buy quantity (${buyQty})`;
        }
      }
    }

    if (formData.getProducts.length === 0) {
      errors.getProducts = 'Please add at least one get product';
    } else if (formData.getProducts.length > 200) {
      errors.getProducts = 'You cannot add more than 200 products to get list';
    }

    formData.getProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`getProduct_${index}_type`] = 'Product Type is necessary';
      }
      if (!product.pricingType) {
        errors[`getProduct_${index}_pricing`] = 'Please select price type (Sales Price or Offer Price)';
      }
      applyZeroPriceProductError(errors, product, index, 'getProduct');
    });

    if (!formData.getQuantity || formData.getQuantity === '') {
      errors.getQuantity = 'Get quantity is required and must be a positive number';
    } else {
      const getQty = parseInt(formData.getQuantity);
      if (isNaN(getQty) || getQty < 1) {
        errors.getQuantity = 'Get quantity must be at least 1';
      } else if (getQty > 9999) {
        errors.getQuantity = 'Get quantity cannot exceed 9999';
      } else {
        const compulsoryCount = formData.getProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (p.quantity || 1), 0);
        if (compulsoryCount > getQty) {
          errors.getQuantity = `Compulsory quantity (${compulsoryCount}) cannot exceed get quantity (${getQty})`;
        }
      }
    }

    if (formData.buyDiscountApplyTo && formData.buyDiscountApplyTo !== 'none') {
      if (formData.buyDiscountApplyTo === 'bulk') {
        if (!formData.buyDiscountValue || formData.buyDiscountValue === '') {
          errors.buyDiscountValue = 'Please enter a discount value';
        } else {
          const discountValue = parseFloat(formData.buyDiscountValue);
          if (isNaN(discountValue)) {
            errors.buyDiscountValue = 'Discount value must be a valid number';
          } else if (formData.buyDiscountType === 'percentage') {
            if (discountValue < 0 || discountValue > 100) {
              errors.buyDiscountValue = 'Discount percentage must be between 0 and 100';
            }
          } else if (formData.buyDiscountType === 'fixed') {
            if (discountValue < 0) {
              errors.buyDiscountValue = 'Fixed discount cannot be negative';
            } else {
              const productsWithInvalidDiscount = formData.buyProducts.filter((product) => {
                const productPrice = getProductUnitPrice(product);
                return productPrice > 0 && discountValue > productPrice;
              });
              if (productsWithInvalidDiscount.length > 0) {
                const productDetails = productsWithInvalidDiscount.map(p => {
                  const price = getProductUnitPrice(p);
                  return `${p.name || p.product_name} (£${price.toFixed(2)})`;
                }).join(', ');
                errors.buyDiscountValue = `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price. Invalid products: ${productDetails}`;
              } else {
                const productsThatWouldBeFree = formData.buyProducts.filter((product) => {
                  const productPrice = getProductUnitPrice(product);
                  return productPrice > 0 && discountValue === productPrice;
                });
                if (productsThatWouldBeFree.length > 0) {
                  const productNames = productsThatWouldBeFree.map(p => p.name || p.product_name).join(', ');
                  errors.buyDiscountValue = `Warning: Fixed discount (£${discountValue.toFixed(2)}) is equal to the price of some products (${productNames}), which may result in those products being free.`;
                }
              }
            }
          }
        }
      } else if (formData.buyDiscountApplyTo === 'per-item') {
        formData.buyProducts.forEach((product, index) => {
          if (!product.buyItemDiscountValue || product.buyItemDiscountValue === '') {
            errors[`buyProduct_${index}_discountValue`] = 'Discount value is required for per-item discount';
          } else {
            const discountValue = parseFloat(product.buyItemDiscountValue);
            if (isNaN(discountValue)) {
              errors[`buyProduct_${index}_discountValue`] = 'Discount value must be a valid number';
            } else if (product.buyItemDiscountType === 'percentage') {
              if (discountValue < 0 || discountValue > 100) {
                errors[`buyProduct_${index}_discountValue`] =
                  'Discount percentage must be between 0 and 100';
              }
            } else if (product.buyItemDiscountType === 'fixed') {
              const productPrice = getProductUnitPrice(product);
              if (discountValue < 0) {
                errors[`buyProduct_${index}_discountValue`] = 'Fixed discount cannot be negative';
              } else if (productPrice > 0 && discountValue >= productPrice) {
                errors[`buyProduct_${index}_discountValue`] =
                  `Fixed discount (£${discountValue.toFixed(2)}) cannot exceed product price (£${productPrice.toFixed(2)})`;
              }
            }
          }
        });
      }
    }

    if (formData.getDiscountType === 'set-value') {
      if (!formData.getSetValue || formData.getSetValue === '') {
        errors.getSetValue = 'Please enter a set value';
      } else {
        const setValue = parseFloat(formData.getSetValue);
        if (isNaN(setValue)) {
          errors.getSetValue = 'Set value must be a valid number';
        } else if (setValue <= 0) {
          errors.getSetValue = 'Set value must be greater than 0';
        } else if (formData.getProducts.length > 0) {
          const getQty = parseInt(formData.getQuantity, 10);
          const normalizedGetQty =
            Number.isFinite(getQty) && getQty > 0 ? getQty : 1;
          const unitPrices = formData.getProducts
            .map((product) => getProductUnitPrice(product))
            .filter((price) => Number.isFinite(price) && price > 0);
          if (unitPrices.length > 0) {
            const minUnitPrice = Math.min(...unitPrices);
            const maxAllowedSetValue = minUnitPrice * normalizedGetQty;
            if (setValue > maxAllowedSetValue) {
              errors.getSetValue =
                `Set value (£${setValue.toFixed(2)}) cannot be greater than the product price (£${maxAllowedSetValue.toFixed(2)})`;
            }
          }
        }
      }
    } else if (formData.getDiscountType === 'value-off') {
      if (formData.getItemDiscountApplyTo === 'bulk') {
        if (!formData.getItemDiscountValue || formData.getItemDiscountValue === '') {
          errors.getItemDiscountValue = 'Please enter a discount value';
        } else {
          const discountValue = parseFloat(formData.getItemDiscountValue);
          if (isNaN(discountValue)) {
            errors.getItemDiscountValue = 'Discount value must be a valid number';
          } else if (formData.getItemDiscountType === 'percentage') {
            if (discountValue < 0 || discountValue > 100) {
              errors.getItemDiscountValue = 'Discount percentage must be between 0 and 100';
            }
          } else if (formData.getItemDiscountType === 'fixed') {
            if (discountValue < 0) {
              errors.getItemDiscountValue = 'Fixed discount cannot be negative';
            } else {
              const productsWithInvalidDiscount = formData.getProducts.filter((product) => {
                const productPrice = getProductUnitPrice(product);
                return productPrice > 0 && discountValue > productPrice;
              });
              if (productsWithInvalidDiscount.length > 0) {
                const productDetails = productsWithInvalidDiscount.map(p => {
                  const price = getProductUnitPrice(p);
                  return `${p.name || p.product_name} (£${price.toFixed(2)})`;
                }).join(', ');
                errors.getItemDiscountValue = `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price. Invalid products: ${productDetails}`;
              } else {
                const productsThatWouldBeFree = formData.getProducts.filter((product) => {
                  const productPrice = getProductUnitPrice(product);
                  return productPrice > 0 && discountValue === productPrice;
                });
                if (productsThatWouldBeFree.length > 0) {
                  const productNames = productsThatWouldBeFree.map(p => p.name || p.product_name).join(', ');
                  errors.getItemDiscountValue = `Warning: Fixed discount (£${discountValue.toFixed(2)}) is equal to the price of some products (${productNames}), which may result in those products being free.`;
                }
              }
            }
          }
        }
      } else if (formData.getItemDiscountApplyTo === 'per-item') {
        formData.getProducts.forEach((product, index) => {
          if (!product.itemDiscountValue || product.itemDiscountValue === '') {
            errors[`getProduct_${index}_discountValue`] = 'Discount value is required for per-item discount';
          } else {
            const discountValue = parseFloat(product.itemDiscountValue);
            if (isNaN(discountValue)) {
              errors[`getProduct_${index}_discountValue`] = 'Discount value must be a valid number';
            } else if (product.itemDiscountType === 'percentage') {
              if (discountValue < 0 || discountValue > 100) {
                errors[`getProduct_${index}_discountValue`] =
                  'Discount percentage must be between 0 and 100';
              }
            } else if (product.itemDiscountType === 'fixed') {
              const productPrice = getProductUnitPrice(product);
              if (discountValue < 0) {
                errors[`getProduct_${index}_discountValue`] = 'Fixed discount cannot be negative';
              } else if (productPrice > 0 && discountValue >= productPrice) {
                errors[`getProduct_${index}_discountValue`] =
                  `Fixed discount (£${discountValue.toFixed(2)}) cannot exceed product price (£${productPrice.toFixed(2)})`;
              }
            }
          }
        });
      }
    }

    return errors;
  };

  const validateGroupOffer = () => {
    const errors = validateBuyXGetYDiscount();
    const allowed = new Set(Array.from({ length: 10 }, (_, i) => String(i + 1)));
    formData.buyProducts.forEach((product, index) => {
      const g = product.groupNo != null && String(product.groupNo).trim() !== '' ? String(product.groupNo) : '';
      if (!allowed.has(g)) {
        errors[`buyProduct_${index}_group`] = 'Group is required. Choose group 1 to 10';
      }
    });

    formData.getProducts.forEach((product, index) => {
      const g = product.groupNo != null && String(product.groupNo).trim() !== '' ? String(product.groupNo) : '';
      if (!allowed.has(g)) {
        errors[`getProduct_${index}_group`] = 'Group is required. Choose group 1 to 10';
      }
    });

    // Group qty is optional. When filled it must stay within the side total,
    // e.g. Buy 2 cannot be split as Group 1 = 2 plus Group 2 = 1 (total 3).
    const validateGroupQtyWithinTotal = (groupKeys, quantities, totalRaw, errorKey, sideLabel) => {
      let sum = 0;
      let filledCount = 0;

      groupKeys.forEach((g) => {
        const raw = quantities?.[g];
        if (raw === undefined || raw === null || String(raw).trim() === '') return;
        const qty = parseInt(raw, 10);
        if (!Number.isFinite(qty) || qty < 1) {
          errors[`${errorKey}_${g}`] = 'Qty must be at least 1';
          return;
        }
        filledCount += 1;
        sum += qty;
      });

      const total = parseInt(totalRaw, 10);
      if (filledCount > 0 && Number.isFinite(total) && total > 0 && sum > total) {
        errors[errorKey] =
          `${sideLabel} group qty total (${sum}) cannot be greater than Total ${sideLabel} Quantity (${total}).`;
      }
    };

    const buyGroups = [
      ...new Set(
        formData.buyProducts
          .map((product) => String(product.groupNo))
          .filter((g) => allowed.has(g))
      ),
    ].sort((a, b) => Number(a) - Number(b));
    validateGroupQtyWithinTotal(
      buyGroups,
      formData.buyGroupQuantities,
      formData.buyQuantity,
      'buyGroupQty',
      'Buy'
    );

    const getGroups = [
      ...new Set(
        formData.getProducts
          .map((product) => String(product.groupNo))
          .filter((g) => allowed.has(g))
      ),
    ].sort((a, b) => Number(a) - Number(b));
    validateGroupQtyWithinTotal(
      getGroups,
      formData.getGroupQuantities,
      formData.getQuantity,
      'getGroupQty',
      'Get'
    );

    return errors;
  };

  const validateBuyXDiscount = () => {
    const errors = {};

    if (formData.buyProducts.length === 0) {
      errors.buyProducts = 'Please add at least one product to the promotion';
    } else if (formData.buyProducts.length > 200) {
      errors.buyProducts = `Maximum 200 products allowed. Current: ${formData.buyProducts.length} products`;
    }

    formData.buyProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`buyProduct_${index}_type`] = 'Please select product type (Box or Piece)';
      }
      if (!product.pricingType) {
        errors[`buyProduct_${index}_pricing`] = 'Please select price type (Sales Price or Offer Price)';
      }
      applyZeroPriceProductError(errors, product, index, 'buyProduct');
      if (product.compulsory && (!product.quantity || product.quantity === '' || parseInt(product.quantity) < 1)) {
        errors[`buyProduct_${index}_quantity`] = 'Quantity is required and must be at least 1 for compulsory products';
      }
    });

    if (!formData.buyQuantity || formData.buyQuantity === '') {
      errors.buyQuantity = 'Buy quantity is required and must be a positive whole number';
    } else {
      const buyQty = parseInt(formData.buyQuantity);
      if (isNaN(buyQty) || buyQty < 1) {
        errors.buyQuantity = 'Buy quantity is required and must be a positive whole number';
      } else if (buyQty > 9999) {
        errors.buyQuantity = 'Buy quantity must be between 1 and 9999';
      } else {
        const compulsoryCount = formData.buyProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (parseInt(p.quantity) || 1), 0);
        if (compulsoryCount > 0 && compulsoryCount > buyQty) {
          errors.compulsoryQuantity = `Total compulsory quantity (${compulsoryCount}) must be less than or equal to buy quantity (${buyQty})`;
        }
      }
    }

    if (formData.buyDiscountConfigType === 'set-value') {
      if (!formData.buySetValue || formData.buySetValue === '') {
        errors.buySetValue = 'Please enter a set value for the promotion';
      } else {
        const setValue = parseFloat(formData.buySetValue);
        if (isNaN(setValue)) {
          errors.buySetValue = 'Please enter a valid numeric value';
        } else if (setValue <= 0) {
          errors.buySetValue = 'Set value must be greater than 0';
        } else if (formData.buyProducts.length > 0) {
          const buyQty = parseInt(formData.buyQuantity, 10);
          const normalizedBuyQty =
            Number.isFinite(buyQty) && buyQty > 0 ? buyQty : 1;
          const unitPrices = formData.buyProducts
            .map((product) => getProductUnitPrice(product))
            .filter((price) => Number.isFinite(price) && price > 0);
          if (unitPrices.length > 0) {
            const minUnitPrice = Math.min(...unitPrices);
            const maxAllowedSetValue = minUnitPrice * normalizedBuyQty;
            if (setValue > maxAllowedSetValue) {
              errors.buySetValue =
                `Set value (£${setValue.toFixed(2)}) cannot be greater than the product price (£${maxAllowedSetValue.toFixed(2)})`;
            }
          }
        }
      }
    } else if (formData.buyDiscountConfigType === 'value-off') {
      if (formData.buyXDiscountApplyTo === 'bulk') {
        if (!formData.buyXDiscountType || formData.buyXDiscountType === 'no-discount') {
          errors.buyXDiscountType = 'Please select discount type (Percentage or Fixed Amount)';
        } else {
          if (!formData.buyXDiscountValue || formData.buyXDiscountValue === '') {
            errors.buyXDiscountValue = 'Please enter a discount value';
          } else {
            const discountValue = parseFloat(formData.buyXDiscountValue);
            if (isNaN(discountValue)) {
              errors.buyXDiscountValue = 'Please enter a valid numeric value';
            } else if (formData.buyXDiscountType === 'percentage') {
              if (discountValue < 0 || discountValue > 100) {
                errors.buyXDiscountValue = 'Percentage must be between 0 and 100';
              }
            } else if (formData.buyXDiscountType === 'fixed') {
              if (discountValue < 0) {
                errors.buyXDiscountValue = 'Discount amount cannot be negative';
              } else {
                const productsWithInvalidDiscount = formData.buyProducts.filter((product) => {
                  const productPrice = getProductUnitPrice(product);
                  return productPrice > 0 && discountValue > productPrice;
                });
                if (productsWithInvalidDiscount.length > 0) {
                  const productDetails = productsWithInvalidDiscount.map(p => {
                    const price = getProductUnitPrice(p);
                    return `${p.name || p.product_name} (£${price.toFixed(2)})`;
                  }).join(', ');
                  errors.buyXDiscountValue = `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price. Invalid products: ${productDetails}`;
                }
              }
            }
          }
        }
      } else if (formData.buyXDiscountApplyTo === 'per-item') {
        formData.buyProducts.forEach((product, index) => {
          if (product.buyXItemDiscountType && product.buyXItemDiscountType !== 'no-discount') {
            if (!product.buyXItemDiscountValue || product.buyXItemDiscountValue === '') {
              errors[`buyProduct_${index}_discountValue`] = 'Please enter a discount value';
            } else {
              const discountValue = parseFloat(product.buyXItemDiscountValue);
              const productPrice = getProductUnitPrice(product);

              if (isNaN(discountValue)) {
                errors[`buyProduct_${index}_discountValue`] = 'Please enter a valid numeric value';
              } else if (product.buyXItemDiscountType === 'percentage') {
                if (discountValue < 0 || discountValue > 100) {
                  errors[`buyProduct_${index}_discountValue`] = 'Percentage must be between 0 and 100';
                }
              } else if (product.buyXItemDiscountType === 'fixed') {
                if (discountValue < 0) {
                  errors[`buyProduct_${index}_discountValue`] = 'Discount amount cannot be negative';
                } else {
                  const productPrice = getProductUnitPrice(product);
                  if (productPrice > 0 && discountValue > productPrice) {
                    const productLabel =
                      product.name || product.product_name || product.code || `Product ${index + 1}`;
                    errors[`buyProduct_${index}_discountValue`] =
                      `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price (£${productPrice.toFixed(2)}). Invalid product: ${productLabel}`;
                  }
                }
              }
            }
          }
        });
      }
    }

    return errors;
  };

  useEffect(() => {
    if (
      formData.promotionType !== 'buy-x-discount' ||
      formData.buyDiscountConfigType !== 'value-off' ||
      formData.buyXDiscountApplyTo !== 'per-item'
    ) {
      return;
    }

    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (/^buyProduct_\d+_discountValue$/.test(key)) {
          delete next[key];
        }
      });

      formData.buyProducts.forEach((product, index) => {
        if (product.buyXItemDiscountType !== 'fixed') {
          return;
        }
        const rawValue = product.buyXItemDiscountValue;
        if (rawValue === '' || rawValue === undefined || rawValue === null) {
          return;
        }
        const discountValue = parseFloat(rawValue);
        if (Number.isNaN(discountValue)) {
          return;
        }
        const productPrice = getProductUnitPrice(product);
        if (productPrice > 0 && discountValue > productPrice) {
          const productLabel =
            product.name || product.product_name || product.code || `Product ${index + 1}`;
          next[`buyProduct_${index}_discountValue`] =
            `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price (£${productPrice.toFixed(2)}). Invalid product: ${productLabel}`;
        }
      });

      return next;
    });
  }, [
    formData.promotionType,
    formData.buyDiscountConfigType,
    formData.buyXDiscountApplyTo,
    formData.buyProducts,
  ]);

  useEffect(() => {
    if (formData.description && formData.description.length > 500) {
      setErrors((prev) => ({
        ...prev,
        description: 'Description cannot exceed 500 characters',
      }));
    } else if (errors.description === 'Description cannot exceed 500 characters') {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (formData.description.trim()) {
          delete newErrors.description;
        } else {
          newErrors.description = 'Description is required';
        }
        return newErrors;
      });
    }
  }, [formData.description]);

  const validateMultiLayerOffer = () => {
    const errors = {};

    if (formData.buyProducts.length === 0) {
      errors.buyProducts = 'Please add at least one product for the multi-layer offer';
    } else if (formData.buyProducts.length > 200) {
      errors.buyProducts = 'Cannot exceed 200 products in a multi-layer offer';
    }

    const buyProductType = formData.buyProductType || 'box-only';

    let referenceBoxPrice = 0;
    let referencePiecePrice = 0;
    if (formData.buyProducts.length > 0) {
      const buyPricingType = formData.buyPricingType || 'sales';
      referenceBoxPrice = getMinMultiLayerBuyPrice(
        formData.buyProducts,
        'Box',
        buyPricingType
      );
      referencePiecePrice = getMinMultiLayerBuyPrice(
        formData.buyProducts,
        'Piece',
        buyPricingType
      );
    }

    formData.buyProducts.forEach((product, index) => {
      const split = product.split;
      let isValidForProductType = true;

      if (buyProductType === 'box-only' && split !== 0 && split !== 2) {
        isValidForProductType = false;
        errors[`buyProduct_${index}_type`] = 'This product does not support Box type. Please remove and select compatible products.';
      } else if (buyProductType === 'piece-only' && split !== 1 && split !== 2) {
        isValidForProductType = false;
        errors[`buyProduct_${index}_type`] = 'This product does not support Piece type. Please remove and select compatible products.';
      }

      if (isValidForProductType && !product.type) {
        errors[`buyProduct_${index}_type`] = 'Please select product type: Box, Piece, or Both';
      }
      applyMultiLayerBuyZeroPriceErrors(errors, product, index);
    });

    if (!formData.slabs || formData.slabs.length === 0) {
      errors.slabs = 'Please add at least one slab configuration';
      return errors;
    }

    const boxRanges = [];
    const pieceRanges = [];
    const packSizeRaw =
      formData.buyProducts.length > 0
        ? formData.buyProducts[0].pack_size ||
        formData.buyProducts[0].unit_per_box ||
        formData.buyProducts[0].pack ||
        1
        : 1;
    const packSize = parseInt(packSizeRaw, 10) > 0 ? parseInt(packSizeRaw, 10) : 1;

    formData.slabs.forEach((slab, index) => {
      if (!slab.slabName || slab.slabName.trim() === '') {
        errors[`slab_${index}_name`] = 'Slab name is required';
      }

      if (slab.boxMinQuantity !== '' || slab.boxMaxQuantity !== '') {
        const boxMin = parseInt(slab.boxMinQuantity);
        const boxMax = parseInt(slab.boxMaxQuantity);

        if (isNaN(boxMin) || isNaN(boxMax)) {
          errors[`slab_${index}_boxRange`] = 'Please enter a valid box quantity';
        } else if (boxMin > boxMax) {
          errors[`slab_${index}_boxRange`] = 'Minimum quantity must be less than or equal to maximum quantity';
        } else {
          boxRanges.push({ slabIndex: index, min: boxMin, max: boxMax, packSize });
        }
      }

      if (!slab.discountType) {
        errors[`slab_${index}_discountType`] = 'Please select discount type';
      }

      if (slab.discountType === 'set-value') {
        if (buyProductType === 'box-only' || buyProductType === 'both') {
          if (!slab.boxProductsValue || slab.boxProductsValue === '') {
            errors[`slab_${index}_boxValue`] = 'Please enter set value for box products';
          } else {
            const boxValue = parseFloat(slab.boxProductsValue);
            if (isNaN(boxValue)) {
              errors[`slab_${index}_boxValue`] = 'Please enter a valid numeric value';
            } else if (boxValue <= 0) {
              errors[`slab_${index}_boxValue`] = 'Set value for boxes must be greater than 0';
            }
          }
        }

        if (buyProductType === 'piece-only' || buyProductType === 'both') {
          if (!slab.pieceProductsValue || slab.pieceProductsValue === '') {
            errors[`slab_${index}_pieceValue`] = 'Please enter set value for piece products';
          } else {
            const pieceValue = parseFloat(slab.pieceProductsValue);
            if (isNaN(pieceValue)) {
              errors[`slab_${index}_pieceValue`] = 'Please enter a valid numeric value';
            } else if (pieceValue <= 0) {
              errors[`slab_${index}_pieceValue`] = 'Set value for pieces must be greater than 0';
            }
          }
        }
      } else if (slab.discountType === 'percentage' || slab.discountType === 'fixed') {
        if (buyProductType === 'box-only' || buyProductType === 'both') {
          if (!slab.boxProductsValue || slab.boxProductsValue === '') {
            errors[`slab_${index}_boxValue`] = 'Please enter discount value for box products';
          } else {
            const boxValue = parseFloat(slab.boxProductsValue);
            if (isNaN(boxValue)) {
              errors[`slab_${index}_boxValue`] = 'Please enter a valid numeric value';
            } else if (boxValue < 0) {
              errors[`slab_${index}_boxValue`] = 'Discount value cannot be negative';
            } else if (slab.discountType === 'percentage' && boxValue > 100) {
              errors[`slab_${index}_boxValue`] = 'Percentage must be between 0 and 100';
            } else if (
              slab.discountType === 'fixed' &&
              referenceBoxPrice > 0 &&
              boxValue > referenceBoxPrice
            ) {
              errors[`slab_${index}_boxValue`] =
                `Fixed discount (£${boxValue.toFixed(2)}) cannot exceed the lowest product price (£${referenceBoxPrice.toFixed(2)})`;
            }
          }
        }

        if (buyProductType === 'piece-only' || buyProductType === 'both') {
          if (!slab.pieceProductsValue || slab.pieceProductsValue === '') {
            errors[`slab_${index}_pieceValue`] = 'Please enter discount value for piece products';
          } else {
            const pieceValue = parseFloat(slab.pieceProductsValue);
            if (isNaN(pieceValue)) {
              errors[`slab_${index}_pieceValue`] = 'Please enter a valid numeric value';
            } else if (pieceValue < 0) {
              errors[`slab_${index}_pieceValue`] = 'Discount value cannot be negative';
            } else if (slab.discountType === 'percentage' && pieceValue > 100) {
              errors[`slab_${index}_pieceValue`] = 'Percentage must be between 0 and 100';
            } else if (
              slab.discountType === 'fixed' &&
              referencePiecePrice > 0 &&
              pieceValue > referencePiecePrice
            ) {
              errors[`slab_${index}_pieceValue`] =
                `Fixed discount (£${pieceValue.toFixed(2)}) cannot exceed the lowest product price (£${referencePiecePrice.toFixed(2)})`;
            }
          }
        }
      }

      if (slab.pieceMinQuantity !== '' || slab.pieceMaxQuantity !== '') {
        const pieceMin = parseInt(slab.pieceMinQuantity);
        const pieceMax = parseInt(slab.pieceMaxQuantity);

        if (isNaN(pieceMin) || isNaN(pieceMax)) {
          errors[`slab_${index}_pieceRange`] = 'Please enter a valid piece quantity';
        } else if (pieceMin > pieceMax) {
          errors[`slab_${index}_pieceRange`] = 'Minimum quantity must be less than or equal to maximum quantity';
        } else {
          pieceRanges.push({ slabIndex: index, min: pieceMin, max: pieceMax });
        }
      }

      if (slab.hasGetProducts) {
        if (!slab.getQuantity || slab.getQuantity === '') {
          errors[`slab_${index}_getQuantity`] = 'Get quantity is required and must be a positive number';
        } else {
          const getQty = parseInt(slab.getQuantity);
          if (isNaN(getQty) || getQty < 1) {
            errors[`slab_${index}_getQuantity`] = 'Get quantity must be at least 1';
          } else if (getQty > 9999) {
            errors[`slab_${index}_getQuantity`] = 'Get quantity cannot exceed 9999';
          }
        }
        if (!slab.getProducts || slab.getProducts.length === 0) {
          errors[`slab_${index}_getProducts`] = 'Please add at least one get product';
        } else {
          slab.getProducts.forEach((product, pIndex) => {
            if (!product.type) {
              errors[`slab_${index}_getProduct_${pIndex}_type`] = 'Please select pricing for get products';
            }
            if (product.pricingType === 'offer' && (!product.offerPrice || parseFloat(product.offerPrice) === 0)) {
              errors[`slab_${index}_getProduct_${pIndex}_offerPrice`] = 'Get products will be free. Please confirm.';
            }
          });
        }
      }
    });

    Object.assign(errors, buildSlabGetQuantityCompulsoryErrors(formData.slabs));

    const overlapMessages = [];

    for (let i = 0; i < boxRanges.length; i++) {
      for (let j = i + 1; j < boxRanges.length; j++) {
        const range1 = boxRanges[i];
        const range2 = boxRanges[j];
        if (
          (range1.min <= range2.max && range1.max >= range2.min) ||
          (range2.min <= range1.max && range2.max >= range1.min)
        ) {
          const slab1Name = formData.slabs[range1.slabIndex]?.slabName || `Slab ${range1.slabIndex + 1}`;
          const slab2Name = formData.slabs[range2.slabIndex]?.slabName || `Slab ${range2.slabIndex + 1}`;
          overlapMessages.push(`Box range [${range1.min}-${range1.max}] in [${slab1Name}] overlaps with [${slab2Name}]`);
        }
      }
    }

    for (let i = 0; i < pieceRanges.length; i++) {
      for (let j = i + 1; j < pieceRanges.length; j++) {
        const range1 = pieceRanges[i];
        const range2 = pieceRanges[j];
        if (
          (range1.min <= range2.max && range1.max >= range2.min) ||
          (range2.min <= range1.max && range2.max >= range1.min)
        ) {
          const slab1Name = formData.slabs[range1.slabIndex]?.slabName || `Slab ${range1.slabIndex + 1}`;
          const slab2Name = formData.slabs[range2.slabIndex]?.slabName || `Slab ${range2.slabIndex + 1}`;
          overlapMessages.push(`Piece range [${range1.min}-${range1.max}] in [${slab1Name}] overlaps with [${slab2Name}]`);
        }
      }
    }

    // if (overlapMessages.length > 0) {
    //   errors.slabOverlap = overlapMessages.length === 1
    //     ? `${overlapMessages[0]}. Please adjust.`
    //     : `Multiple overlaps detected: ${overlapMessages.join('; ')}. Please adjust all ranges.`;
    // }

    if (formData.slabs && formData.slabs.length > 0) {
      formData.slabs.forEach((slab, index) => {
        if (
          slab.productType === 'both' &&
          slab.boxMinQuantity &&
          slab.boxMaxQuantity &&
          slab.pieceMinQuantity &&
          slab.pieceMaxQuantity
        ) {
          const boxMin = parseInt(slab.boxMinQuantity);
          const boxMax = parseInt(slab.boxMaxQuantity);
          const pieceMin = parseInt(slab.pieceMinQuantity);
          const pieceMax = parseInt(slab.pieceMaxQuantity);
          const packSize =
            formData.buyProducts.length > 0 ? formData.buyProducts[0].pack_size || 1 : 1;

          const boxMinInPieces = boxMin * packSize;
          const boxMaxInPieces = boxMax * packSize;

          if (
            (pieceMin <= boxMaxInPieces && pieceMax >= boxMinInPieces) ||
            (boxMinInPieces <= pieceMax && boxMaxInPieces >= pieceMin)
          ) {
            errors[`slab_${index}_conflict`] =
              `Quantity conflict: ${pieceMin}-${pieceMax} pieces and ${boxMin}-${boxMax} boxes overlap (${boxMin}-${boxMax} boxes = ${boxMinInPieces}-${boxMaxInPieces} pieces based on pack size of ${packSize}). Please adjust quantities.`;
          }
        }
      });
    }

    return errors;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (formData.description.length > 500) newErrors.description = 'Description cannot exceed 500 characters';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    } else {
      const endDay = moment(formData.endDate).startOf('day');
      const today = moment().startOf('day');
      if (endDay.isBefore(today)) {
        newErrors.endDate = 'End date cannot be in the past';
      }
    }
    if (formData.startDate && formData.endDate && !newErrors.endDate) {
      const startDay = moment(formData.startDate).startOf('day');
      const endDay = moment(formData.endDate).startOf('day');
      if (endDay.isBefore(startDay)) {
        newErrors.endDate = 'End date cannot be before start date';
      } else {
        const daysDifference = endDay.diff(startDay, 'days');
        if (daysDifference > 365) {
          newErrors.endDate = 'Promotion period cannot exceed 1 year. Please adjust your dates.';
        }
      }
    }
    if (formData.selectedChannels.length === 0)
      newErrors.channels = 'At least one channel is required';

    if (formData.promotionType === 'buy-x-get-y-free') {
      const promotionErrors = validateBuyXGetYFree();
      Object.assign(newErrors, promotionErrors);
    } else if (formData.promotionType === 'buy-x-get-y-discount') {
      const promotionErrors = validateBuyXGetYDiscount();
      Object.assign(newErrors, promotionErrors);
    } else if (formData.promotionType === 'buy-x-discount') {
      const promotionErrors = validateBuyXDiscount();
      Object.assign(newErrors, promotionErrors);
    } else if (formData.promotionType === 'multi-layer-offer') {
      const promotionErrors = validateMultiLayerOffer();
      Object.assign(newErrors, promotionErrors);
    } else if (formData.promotionType === 'group-offer') {
      const promotionErrors = validateGroupOffer();
      Object.assign(newErrors, promotionErrors);
    }

    setErrors(newErrors);
    return newErrors;
  };

  const transformFormDataToAPI = () => {
    const basePayload = {
      promo_id: id,
      title: formData.title,
      description: formData.description,
      type: formData.promotionType,
      start_date: formData.startDate ? moment(formData.startDate).format('YYYY-MM-DD') : '',
      end_date: formData.endDate ? moment(formData.endDate).format('YYYY-MM-DD') : '',
      channels: formData.selectedChannels,
    };

    if (formData.promotionType === 'multi-layer-offer') {
      basePayload.buy_pricing_type = toApiPromotionPricingType(formData.buyPricingType || 'sales');
    }

    let config = {};

    if (formData.promotionType === 'buy-x-get-y-free') {
      config = {
        buy_quantity: parseInt(formData.buyQuantity) || 0,
        get_quantity: parseInt(formData.getQuantity) || 0,
        buy_products: formData.buyProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type || 'Box',
          pricing_type: product.pricingType || 'sales',
          is_compulsory: product.compulsory || false,
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
        })),
        get_products: formData.getProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type || 'Box',
          pricing_type: product.pricingType || 'sales',
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          is_compulsory: product.compulsory || false,
          offer_price: product.offerPrice || 0,
        })),
      };
    } else if (formData.promotionType === 'buy-x-get-y-discount') {

      const getDiscount = {
        discount_config_type: formData.getDiscountType || 'set-value',
        discount_type:
          formData.getDiscountType === 'set-value'
            ? 'set-value'
            : formData.getItemDiscountType || 'percentage',
        discount_value:
          formData.getDiscountType === 'set-value'
            ? parseFloat(formData.getSetValue) || 0
            : parseFloat(formData.getItemDiscountValue) || 0,
        apply_to:
          formData.getDiscountType === 'set-value'
            ? ''
            : (formData.getItemDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN'),
      };



      const buyDiscountConfig = {};

      // Only include buy_discount when discount is actually enabled (not 'none')
      if (formData.buyDiscountApplyTo && formData.buyDiscountApplyTo !== 'none') {
        buyDiscountConfig.buy_discount = {
          apply_to: formData.buyDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN',
          discount_type: formData.buyDiscountType || 'percentage',
          discount_value: parseFloat(formData.buyDiscountValue) || 0,
        };
      }

      config = {
        buy_quantity: parseInt(formData.buyQuantity) || 0,
        get_quantity: parseInt(formData.getQuantity) || 0,
        ...buyDiscountConfig,
        get_discount: getDiscount,
        buy_products: formData.buyProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type,
          pricing_type: product.pricingType || 'sales',
          is_compulsory: product.compulsory || false,
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          item_discount:
            formData.buyDiscountApplyTo === 'per-item'
              ? {
                discount_type: product.buyItemDiscountType || 'percentage',
                discount_value: parseFloat(product.buyItemDiscountValue) || 0,
              }
              : null,
        })),
        get_products: formData.getProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type,
          pricing_type: product.pricingType || 'sales',
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          is_compulsory: product.compulsory || false,
          item_discount:
            formData.getItemDiscountApplyTo === 'per-item'
              ? {
                discount_type: product.itemDiscountType || 'percentage',
                discount_value: parseFloat(product.itemDiscountValue) || 0,
              }
              : null,
        })),
      };
    } else if (formData.promotionType === 'buy-x-discount') {
      const buyDiscountConfig = {};

      // Only add buy_discount when buyDiscountConfigType is 'set-value'
      if (formData.buyDiscountConfigType === 'set-value') {
        buyDiscountConfig.buy_discount = {
          discount_config_type: 'set-value',
          discount_type: 'set-value',
          discount_value: parseFloat(formData.buySetValue) || 0,
          apply_to: '',
        };
      } else if (formData.buyDiscountConfigType === 'value-off') {
        // When value-off is selected, use buy_discount with the actual discount type
        buyDiscountConfig.buy_discount = {
          discount_config_type: 'value-off',
          discount_type: formData.buyXDiscountType === 'no-discount' ? 'percentage' : (formData.buyXDiscountType || 'percentage'),
          discount_value: parseFloat(formData.buyXDiscountValue) || 0,
          apply_to: formData.buyXDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN',
        };
      }

      config = {
        buy_quantity: parseInt(formData.buyQuantity) || 0,
        ...buyDiscountConfig,
        buy_products: formData.buyProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type,
          pricing_type: product.pricingType || 'sales',
          is_compulsory: product.compulsory || false,
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          item_discount:
            formData.buyXDiscountApplyTo === 'per-item'
              ? {
                discount_type: product.buyXItemDiscountType || 'percentage',
                discount_value: parseFloat(product.buyXItemDiscountValue) || 0,
              }
              : null,
        })),
      };
    } else if (formData.promotionType === 'multi-layer-offer') {
      const slabs = formData.slabs || [];
      const parseQty = (val) => {
        const num = parseInt(val, 10);
        return Number.isFinite(num) && num >= 0 ? num : 0;
      };
      const buildThresholdRangeMap = (slabList, minKey, maxKey) => {
        const thresholdRows = slabList
          .map((slab, index) => {
            const fromValue = parseQty(slab[minKey]);
            const toValue = parseQty(slab[maxKey]);
            return {
              index,
              threshold: toValue,
              isSingleValue: fromValue > 0 && fromValue === toValue,
            };
          })
          .filter((row) => row.isSingleValue)
          .sort((a, b) => a.threshold - b.threshold);

        const rangeMap = new Map();
        thresholdRows.forEach((row, idx) => {
          const next = thresholdRows[idx + 1];
          rangeMap.set(row.index, {
            from: row.threshold,
            // to: next ? Math.max(row.threshold, next.threshold - 1) : row.threshold,
            to: row.threshold,
          });
        });
        return rangeMap;
      };
      const boxThresholdRangeMap = buildThresholdRangeMap(slabs, 'boxMinQuantity', 'boxMaxQuantity');
      const pieceThresholdRangeMap = buildThresholdRangeMap(slabs, 'pieceMinQuantity', 'pieceMaxQuantity');

      const buyPricingType = formData.buyPricingType || 'sales';
      config = {
        product_type: formData.buyProductType || 'box-only',
        buy_pricing_type: toApiPromotionPricingType(buyPricingType),
        buy_products: formData.buyProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type || 'Box',
          box_pricing_type: toApiPromotionPricingType(
            getMultiLayerBuyPricingMode(product, 'Box', buyPricingType)
          ),
          piece_pricing_type: toApiPromotionPricingType(
            getMultiLayerBuyPricingMode(product, 'Piece', buyPricingType)
          ),
          is_compulsory: product.compulsory || false,
        })),
        slabs: slabs.map((slab, index) => {
          const boxFrom = parseQty(slab.boxMinQuantity);
          const boxTo = parseQty(slab.boxMaxQuantity);
          const pieceFrom = parseQty(slab.pieceMinQuantity);
          const pieceTo = parseQty(slab.pieceMaxQuantity);
          const normalizedBoxRange = boxThresholdRangeMap.get(index) || { from: boxFrom, to: boxTo };
          const normalizedPieceRange = pieceThresholdRangeMap.get(index) || { from: pieceFrom, to: pieceTo };

          return {
            slab_number: index + 1,
            slab_name: slab.slabName,
            slab_order: slab.slabOrder || index + 1,
            product_type: formData.buyProductType || 'box-only',
            box_quantity_range: {
              from: normalizedBoxRange.from,
              to: normalizedBoxRange.to,
            },
            piece_quantity_range: {
              from: normalizedPieceRange.from,
              to: normalizedPieceRange.to,
            },
            box_pricing_type: toApiPromotionPricingType(buyPricingType),
            box_discount_type: slab.discountType || 'percentage',
            box_discount_value: parseFloat(slab.boxProductsValue) || 0,
            piece_pricing_type: toApiPromotionPricingType(buyPricingType),
            piece_discount_type: slab.discountType || 'percentage',
            piece_discount_value: parseFloat(slab.pieceProductsValue) || 0,
            include_get_products: slab.hasGetProducts || false,
            get_quantity: slab.hasGetProducts ? (parseInt(slab.getQuantity) || 0) : 0,
            get_products: slab.hasGetProducts
              ? (slab.getProducts || []).map((product) => ({
                product_id: product.id,
                product_type: product.type || 'Piece',
                quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
                is_compulsory: product.compulsory || false,
                compulsory_quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
                pricing_type: toApiPromotionPricingType(product.pricingType || buyPricingType),
                discount_type: 'free',
                discount_value: 0,
              }))
              : [],
          };
        }),
      };
    } else if (formData.promotionType === 'group-offer') {
      const getDiscount = {
        discount_config_type: formData.getDiscountType || 'set-value',
        discount_type:
          formData.getDiscountType === 'set-value'
            ? 'set-value'
            : formData.getItemDiscountType || 'percentage',
        discount_value:
          formData.getDiscountType === 'set-value'
            ? parseFloat(formData.getSetValue) || 0
            : parseFloat(formData.getItemDiscountValue) || 0,
        apply_to:
          formData.getDiscountType === 'set-value'
            ? ''
            : (formData.getItemDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN'),
      };

      const buyDiscountConfig = {};

      if (formData.buyDiscountApplyTo && formData.buyDiscountApplyTo !== 'none') {
        buyDiscountConfig.buy_discount = {
          apply_to: formData.buyDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN',
          discount_type: formData.buyDiscountType || 'percentage',
          discount_value: parseFloat(formData.buyDiscountValue) || 0,
        };
      }

      const groupIdFromProduct = (product) => {
        const n = parseInt(String(product.groupNo), 10);
        return Number.isFinite(n) && n >= 1 && n <= 10 ? String(n) : null;
      };

      const buyGroupQuantities = formData.buyGroupQuantities || {};
      const getGroupQuantities = formData.getGroupQuantities || {};
      // Group qty is optional; 0 lets the engine fall back to its default of one per group.
      const resolveGroupQty = (groupId, map) => {
        if (!groupId) return 0;
        const qty = parseInt(map[groupId], 10);
        return Number.isFinite(qty) && qty > 0 ? qty : 0;
      };

      config = {
        buy_quantity: parseInt(formData.buyQuantity) || 0,
        get_quantity: parseInt(formData.getQuantity) || 0,
        ...buyDiscountConfig,
        get_discount: getDiscount,
        buy_products: formData.buyProducts.map((product) => {
          const groupId = groupIdFromProduct(product);
          return {
            product_id: String(product.id),
            product_type: product.type,
            pricing_type: product.pricingType || 'sales',
            is_compulsory: product.compulsory || false,
            quantity: resolveGroupQty(groupId, buyGroupQuantities),
            ...(groupId != null ? { group_id: groupId } : {}),
            item_discount:
              formData.buyDiscountApplyTo === 'per-item'
                ? {
                  discount_type: product.buyItemDiscountType || 'percentage',
                  discount_value: parseFloat(product.buyItemDiscountValue) || 0,
                }
                : null,
          };
        }),
        get_products: formData.getProducts.map((product) => {
          const groupId = groupIdFromProduct(product);
          return {
            product_id: String(product.id),
            product_type: product.type,
            pricing_type: product.pricingType || 'sales',
            is_compulsory: product.compulsory || false,
            quantity: resolveGroupQty(groupId, getGroupQuantities),
            ...(groupId != null ? { group_id: groupId } : {}),
            item_discount:
              formData.getItemDiscountApplyTo === 'per-item'
                ? {
                  discount_type: product.itemDiscountType || 'percentage',
                  discount_value: parseFloat(product.itemDiscountValue) || 0,
                }
                : null,
          };
        }),
      };
    }

    return {
      ...basePayload,
      config,
    };
  };

  const checkForZeroPriceProducts = () => {
    const productsWithZeroPrice = [];

    const checkProduct = (product, listType) => {
      const pricingType = product.pricingType || 'sales';
      const price = parseFloat(getProductUnitPrice(product)) || 0;

      if (price === 0) {
        productsWithZeroPrice.push({
          name: product.name,
          code: product.code,
          pricingType,
          listType,
        });
      }
    };

    const promoType = formData.promotionType;

    if (promoType === 'buy-x-discount') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
    } else if (promoType === 'buy-x-get-y-discount') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
      formData.getProducts?.forEach(product => checkProduct(product, 'Get Products'));
    } else if (promoType === 'buy-x-get-y-free') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
    } else if (promoType === 'multi-layer-offer') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
    } else if (promoType === 'group-offer') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
      formData.getProducts?.forEach(product => checkProduct(product, 'Get Products'));
    }

    return productsWithZeroPrice;
  };

  const handleSave = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      showToast('Please fix all validation errors before submitting', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = transformFormDataToAPI();

      const response = await updatePromotion(payload);

      if (response.success) {
        showToast('Promotion updated successfully!', 'success');
        setTimeout(() => {
          navigate('/Promotions/Promos');
        }, 1500);
      } else {
        let errorMessage = response.message || 'Failed to update promotion';

        if (typeof errorMessage === 'string') {
          errorMessage = errorMessage.replace(/^Validation error:\s*/i, '');
        }

        if (response.errors && Object.keys(response.errors).length > 0) {
          const errorDetails = Object.entries(response.errors)
            .map(([field, message]) => {
              if (typeof message === 'string') {
                return message.replace(/^Validation error:\s*/i, '');
              }
              return message;
            })
            .join(', ');
          errorMessage += ': ' + errorDetails;
        }

        showToast(errorMessage, 'error');
      }
    } catch (error) {
      const errorMsg = error.message || 'Unknown error';
      const cleanErrorMsg = typeof errorMsg === 'string'
        ? errorMsg.replace(/^Validation error:\s*/i, '')
        : errorMsg;
      showToast(cleanErrorMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmZeroPriceWarning = async () => {
    setShowZeroPriceWarning(false);
    setZeroPriceProducts([]);
    setSaving(true);
    try {
      const payload = transformFormDataToAPI();

      const response = await updatePromotion(payload);

      if (response.success) {
        showToast('Promotion updated successfully!', 'success');
        setTimeout(() => {
          navigate('/Promotions/Promos');
        }, 1500);
      } else {
        let errorMessage = response.message || 'Failed to update promotion';

        if (typeof errorMessage === 'string') {
          errorMessage = errorMessage.replace(/^Validation error:\s*/i, '');
        }

        if (response.errors && Object.keys(response.errors).length > 0) {
          const errorDetails = Object.entries(response.errors)
            .map(([field, message]) => {
              if (typeof message === 'string') {
                return message.replace(/^Validation error:\s*/i, '');
              }
              return message;
            })
            .join(', ');
          errorMessage += ': ' + errorDetails;
        }

        showToast(errorMessage, 'error');
      }
    } catch (error) {
      const errorMsg = error.message || 'Unknown error';
      const cleanErrorMsg = typeof errorMsg === 'string'
        ? errorMsg.replace(/^Validation error:\s*/i, '')
        : errorMsg;
      showToast(cleanErrorMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelZeroPriceWarning = () => {
    setShowZeroPriceWarning(false);
    setZeroPriceProducts([]);
  };

  const renderPromotionTypeComponent = () => {
    const commonProps = {
      formData,
      handleInputChange,
      handleOpenProductModal,
      handleRemoveProduct,
      handleProductCompulsoryChange,
      handleProductQuantityChange,
      handleProductTypeChange,
      handleProductPricingChange,
      buyProductsSearch,
      setBuyProductsSearch,
      getProductsSearch,
      setGetProductsSearch,
      handleSlabProductQuantityChange,
      handleSlabProductPricingChange,
      errors,
    };

    switch (formData.promotionType) {
      case 'buy-x-get-y-free':
        return <BuyXGetYFree {...commonProps} />;
      case 'buy-x-get-y-discount':
        return <BuyXGetYDiscount {...commonProps} />;
      case 'buy-x-discount':
        return <BuyXDiscount {...commonProps} />;
      case 'multi-layer-offer':
        return (
          <MultiLayerOffer
            {...commonProps}
            multiLayerStep={multiLayerStep}
            setMultiLayerStep={setMultiLayerStep}
            setModalType={setModalType}
            setCurrentSlabIndex={setCurrentSlabIndex}
            setModalTitle={setModalTitle}
            setShowProductModal={setShowProductModal}
            handleSlabRemoveProduct={handleSlabRemoveProduct}
            handleSlabProductTypeChange={handleSlabProductTypeChange}
            handleSlabProductCompulsoryChange={handleSlabProductCompulsoryChange}
          />
        );
      case 'group-offer':
        return (
          <GroupOffer
            {...commonProps}
            handleProductItemDiscountTypeChange={handleProductItemDiscountTypeChange}
            handleProductItemDiscountValueChange={handleProductItemDiscountValueChange}
            readOnly={isViewMode}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div>
        <Box
          sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading promotion data...
          </Typography>
        </Box>

      </div>
    );
  }

  if (error) {
    return (
      <div>

        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={() => navigate('/Promotions/Promos')}>
            Back to Promotions
          </Button>
        </Box>

      </div>
    );
  }

  const handleCancel = () => {
    navigate('/Promotions/Promos');
  };

  return (
    <>
      <Box sx={{ p: 1.5, backgroundColor: "#f5f7fa" }}>
        <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: 2 }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Promotion Details
                </Typography>
                <Divider color="black" />
                <Grid container spacing={2} mt={0.5}>
                  <Grid item xs={6}>
                    <TextField
                      className="form-input-border text-field"
                      fullWidth
                      label="Promotion Title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      error={!!errors.title}
                      helperText={errors.title}
                      placeholder="Enter promotion title"
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <DatePicker
                      className="form-input-border text-field"
                      label="Start Date"
                      value={formData.startDate}
                      onChange={(date) => handleInputChange('startDate', date)}
                      format="DD-MM-YYYY"
                      minDate={moment()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.startDate,
                          helperText: errors.startDate,
                          placeholder: 'DD-MM-YYYY',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      className="form-input-border text-field"
                      label="End Date"
                      value={formData.endDate}
                      onChange={(date) => handleInputChange('endDate', date)}
                      format="DD-MM-YYYY"
                      minDate={resolveEndDateMinDate(formData.startDate)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.endDate,
                          helperText: errors.endDate,
                          placeholder: 'DD-MM-YYYY',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      className="form-input-border text-field"
                      fullWidth
                      label="Promotion Description"
                      value={formData.description}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 500) {
                          handleInputChange('description', value);
                        }
                      }}
                      error={!!errors.description}
                      helperText={
                        
                        errors.description ||
                        `${formData.description.length}/500 characters`
                      }
                      FormHelperTextProps={{
                        sx: {
                          color: "#000",
                        },
                      }}
                      placeholder="Enter customer-facing description"
                      multiline
                      minRows={2}
                      maxRows={5}
                      inputProps={{ maxLength: 500 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 500 }}>
                      Promotion Channel
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {Array.isArray(channels) && channels.length > 0 ? (
                        channels.map((channel) => {
                          const isChecked = channel.isMobileApp
                            ? mobileAppChannelValue && formData.selectedChannels.includes(mobileAppChannelValue)
                            : formData.selectedChannels.includes(channel.value);
                          return (
                            <FormControlLabel
                              key={channel.id}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={() => handleChannelToggle(channel.value)}
                                  className="checkBox"
                                />
                              }
                              label={channel.label}
                            />
                          );
                        })
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Loading channels...
                        </Typography>
                      )}
                    </Stack>
                    {errors.channels && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {errors.channels}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    color: "#212b36",
                    mb: 0.5,
                  }}
                >
                  Select Promotion Type
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    width: '100%',
                    alignItems: 'stretch',
                    gridTemplateColumns: {
                      xs: 'minmax(0, 1fr)',
                      sm: 'repeat(3, minmax(0, 1fr))',
                      md: 'repeat(3, minmax(0, 1fr))',
                      lg: 'repeat(5, minmax(0, 1fr))',
                    },
                  }}
                >
                  {promotionTypes.map((type) => (
                    <Card
                      key={type.id}
                      onClick={() => handleInputChange('promotionType', type.id)}
                      elevation={0}
                      sx={{
                        bgcolor: formData.promotionType === type.id ? '#e3f2fd' : '#f5f7fa',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        height: '100%',
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: 0,
                        border: formData.promotionType === type.id ? '2px solid #2196f3' : '1px solid rgba(59, 59, 59, 0.94)',
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          textAlign: 'left',
                          minHeight: 0,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                            color: '#212b36',
                            mb: 1,
                          }}
                        >
                          {type.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            lineHeight: 1.5,
                            fontSize: '0.75rem',
                            color: '#212b36',
                            flex: 1,
                          }}
                        >
                          {type.description}
                        </Typography>
                      </Box>
                    </Card>
                  ))}
                </Box>
                {errors.promotionType && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {errors.promotionType}
                  </Typography>
                )}
              </Box>

              {formData.promotionType === 'buy-x-get-y-free' && (
                <BuyXGetYFree
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleOpenProductModal={handleOpenProductModal}
                  handleRemoveProduct={handleRemoveProduct}
                  handleProductCompulsoryChange={handleProductCompulsoryChange}
                  handleProductQuantityChange={handleProductQuantityChange}
                  handleProductTypeChange={handleProductTypeChange}
                  handleProductPricingChange={handleProductPricingChange}
                  buyProductsSearch={buyProductsSearch}
                  setBuyProductsSearch={setBuyProductsSearch}
                  getProductsSearch={getProductsSearch}
                  setGetProductsSearch={setGetProductsSearch}
                  errors={errors}
                  isViewMode={isViewMode}
                />
              )}

              {formData.promotionType === 'buy-x-get-y-discount' && (
                <BuyXGetYDiscount
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleOpenProductModal={handleOpenProductModal}
                  handleRemoveProduct={handleRemoveProduct}
                  handleProductCompulsoryChange={handleProductCompulsoryChange}
                  handleProductQuantityChange={handleProductQuantityChange}
                  handleProductTypeChange={handleProductTypeChange}
                  handleProductPricingChange={handleProductPricingChange}
                  handleProductItemDiscountTypeChange={handleProductItemDiscountTypeChange}
                  handleProductItemDiscountValueChange={handleProductItemDiscountValueChange}
                  buyProductsSearch={buyProductsSearch}
                  setBuyProductsSearch={setBuyProductsSearch}
                  getProductsSearch={getProductsSearch}
                  setGetProductsSearch={setGetProductsSearch}
                  errors={errors}
                  isViewMode={isViewMode}
                />
              )}

              {formData.promotionType === 'buy-x-discount' && (
                <BuyXDiscount
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleOpenProductModal={handleOpenProductModal}
                  handleRemoveProduct={handleRemoveProduct}
                  handleProductCompulsoryChange={handleProductCompulsoryChange}
                  handleProductTypeChange={handleProductTypeChange}
                  handleProductPricingChange={handleProductPricingChange}
                  buyProductsSearch={buyProductsSearch}
                  setBuyProductsSearch={setBuyProductsSearch}
                  errors={errors}
                  isViewMode={isViewMode}
                />
              )}

              {formData.promotionType === 'multi-layer-offer' && (
              <MultiLayerOffer
                formData={formData}
                handleInputChange={handleInputChange}
                handleOpenProductModal={handleOpenProductModal}
                handleRemoveProduct={handleRemoveProduct}
                handleProductCompulsoryChange={handleProductCompulsoryChange}
                handleProductQuantityChange={handleProductQuantityChange}
                handleProductTypeChange={handleProductTypeChange}
                handleProductPricingChange={handleProductPricingChange}
                multiLayerStep={multiLayerStep}
                setMultiLayerStep={setMultiLayerStep}
                setModalType={setModalType}
                setCurrentSlabIndex={setCurrentSlabIndex}
                setModalTitle={setModalTitle}
                setShowProductModal={setShowProductModal}
                handleSlabRemoveProduct={handleSlabRemoveProduct}
                handleSlabProductTypeChange={handleSlabProductTypeChange}
                handleSlabProductCompulsoryChange={handleSlabProductCompulsoryChange}
                handleSlabProductQuantityChange={handleSlabProductQuantityChange}
                handleSlabProductPricingChange={handleSlabProductPricingChange}
                buyProductsSearch={buyProductsSearch}
                setBuyProductsSearch={setBuyProductsSearch}
                errors={errors}
                isViewMode={isViewMode}
              />
            )}

              {formData.promotionType === 'group-offer' && (
                <GroupOffer
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleOpenProductModal={handleOpenProductModal}
                  handleRemoveProduct={handleRemoveProduct}
                  handleProductCompulsoryChange={handleProductCompulsoryChange}
                  handleProductQuantityChange={handleProductQuantityChange}
                  handleProductTypeChange={handleProductTypeChange}
                  handleProductPricingChange={handleProductPricingChange}
                  handleProductItemDiscountTypeChange={handleProductItemDiscountTypeChange}
                  handleProductItemDiscountValueChange={handleProductItemDiscountValueChange}
                  buyProductsSearch={buyProductsSearch}
                  setBuyProductsSearch={setBuyProductsSearch}
                  getProductsSearch={getProductsSearch}
                  setGetProductsSearch={setGetProductsSearch}
                  errors={errors}
                  readOnly={isViewMode}
                />
              )}
              <Divider color="black" mt={1} />
              <Stack direction="row" spacing={2} justifyContent="space-between" my={2}>
                {formData.promotionType === 'multi-layer-offer' ? (
                  <>
                    <Button variant="outlined" onClick={handleCancel} className="button-variant-outlined">
                      Cancel
                    </Button>
                    <Stack direction="row" spacing={2}>
                      {multiLayerStep === 2 && (
                        <Button
                          variant="outlined"
                          startIcon={<Iconify icon="eva:arrow-back-fill" />}
                          onClick={() => setMultiLayerStep(1)}
                          className="button-variant-outlined"
                        >
                          Previous
                        </Button>
                      )}
                      {multiLayerStep === 1 ? (
                        <Button
                          
                          className="button-variant-contained"
                          endIcon={<Iconify icon="eva:arrow-forward-fill" />}
                          onClick={() => setMultiLayerStep(2)}
                        
                        >
                          Next
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          className="button-variant-contained"
                          onClick={handleSave}
                          disabled={saving}
                          // sx={{ backgroundColor: '#003DA5' }}
                          startIcon={
                            saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null
                          }
                        >
                          {saving ? 'Updating...' : 'Update Promotion'}
                        </Button>
                      )}
                    </Stack>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      className="button-variant-outlined"
                      onClick={handleCancel} disabled={saving}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      className="button-variant-contained"
                      onClick={handleSave}
                      disabled={saving}

                      startIcon={
                        saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null
                      }
                    >
                      {saving ? 'Updating...' : 'Update Promotion'}
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
          </LocalizationProvider>

          <ProductSelectionModal
            open={showProductModal}
            onClose={handleCloseProductModal}
            onConfirm={handleConfirmProducts}
            title={modalTitle}
            selectedProducts={
              modalType === 'getForSlab' && currentSlabIndex !== null
                ? formData.slabs?.[currentSlabIndex]?.getProducts || []
                : formData[modalType === 'buy' ? 'buyProducts' : 'getProducts']
            }
            productTypeFilter={
              formData.promotionType === 'multi-layer-offer' && (modalType === 'buy' || modalType === 'getForSlab')
                ? formData.buyProductType
                : null
            }
            firstBuyProduct={
              formData.promotionType === 'multi-layer-offer' &&
                modalType === 'buy' &&
                formData.buyProducts &&
                formData.buyProducts.length > 0
                ? formData.buyProducts[0]
                : null
            }
            isGetForSlab={modalType === 'getForSlab'}
            alreadyAddedProducts={[]}
            showToast={showToast}
          />

          <Dialog
            open={showZeroPriceWarning}
            onClose={handleCancelZeroPriceWarning}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ bgcolor: '#FFF4E5', color: '#FF9800', borderBottom: '1px solid #FFE0B2' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Iconify icon="eva:alert-triangle-fill" width={24} />
                <Typography variant="h6" component="span">
                  Zero Price Warning
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Products will be offered for free. Is this correct?
              </Alert>

              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                The following products have zero pricing:
              </Typography>

              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {zeroPriceProducts.map((product, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      bgcolor: '#FFF4E5',
                      borderRadius: 1,
                      border: '1px solid #FFE0B2',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#212B36' }}>
                      {product.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#637381' }}>
                      Code: {product.code} | Pricing: {product.pricingType} | Location: {product.listType}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Typography variant="body2" sx={{ mt: 2, color: '#637381' }}>
                If you proceed, these products will be free in this promotion.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={handleCancelZeroPriceWarning} variant="outlined">
                Cancel & Review
              </Button>
              <Button
                onClick={handleConfirmZeroPriceWarning}
                variant="contained"
                color="warning"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
              >
                {saving ? 'Updating...' : 'Yes, Proceed'}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={toast.open}
            autoHideDuration={6000}
            onClose={handleCloseToast}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseToast}
              severity={toast.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </>
  );
}
